import React, { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DEFAULTS, { MODEL_MAP, DEFAULT_MODEL, CONVEYOR_LABELS, LABEL_MAPPING, SPECIAL_LABELS, ALL_ITEM_LABELS, ATBL_ALT, LENGTH_LIMIT_MAX_FT, extractATBLDocs, pickATBLDoc, deriveModuleOptionStates } from '../../../utils/defaultValues';
import { computeGrandTotal } from '../../../utils/quoteTotals';
import { useDashboard } from '../../../context/dashboardContext';
import { useAuth } from '../../../context/authContext';
import toast from 'react-hot-toast';
import { stateReducer, initialState, createAliases } from './states';

// Image imports used by handlers
import tenFtModel from '../../../assets/images/10ft-model.jpeg';
import tenFtForkModel from '../../../assets/images/10ft-fork-model.jpg';
import liftTable from '../../../assets/images/lift-table.jpeg';
import fiveFtModel from '../../../assets/images/5ft-model.png';

// ─── User-added parts: localStorage fallback ────────────────────────────────
// User-added rows (rows created via "+ New Item", including their drag-and-
// drop placement: `userSection` + `sortOrder`) ride on the `parts` payload
// to the backend. Until the backend persists `parts`, mirror those rows to
// localStorage keyed by quote_number so they survive a save→reload round
// trip on the same browser. When the backend starts returning `quote.parts`,
// it takes precedence — this fallback simply goes unused.
const USER_PARTS_LS_PREFIX = 'babs:userParts:';
const userPartsKey = (quoteNumber) => quoteNumber ? `${USER_PARTS_LS_PREFIX}${quoteNumber}` : null;

const saveUserPartsToLocal = (quoteNumber, parts) => {
  try {
    const key = userPartsKey(quoteNumber);
    if (!key) return;
    const userParts = (parts || []).filter(p => p && p.isUserAdded && !p.isDeleted);
    if (userParts.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(userParts));
    }
  } catch (_) { /* localStorage may be unavailable; ignore */ }
};

const loadUserPartsFromLocal = (quoteNumber) => {
  try {
    const key = userPartsKey(quoteNumber);
    if (!key) return [];
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(p => p && p.isUserAdded) : [];
  } catch (_) { return []; }
};

// Copy the localStorage entry from one quote_number key to another (used
// when the server bumps the version on update so the new version key has
// the same user rows as the old one).
const carryUserPartsToNewVersion = (oldQuoteNumber, newQuoteNumber, partsFromSave) => {
  if (!newQuoteNumber || newQuoteNumber === oldQuoteNumber) {
    saveUserPartsToLocal(newQuoteNumber || oldQuoteNumber, partsFromSave);
    return;
  }
  saveUserPartsToLocal(newQuoteNumber, partsFromSave);
};

// Build the `parts` payload sent to the server. Only user-added rows
// (created via "+ New Item") are persisted here; conveyor-derived rows
// are rebuilt from infeed_modules/outfeed_modules on load. Transient UI
// flags (isEditing/isNew) are stripped so they don't leak into mongo.
const serializeUserPartsForSave = (sampleData) => {
  return (sampleData || [])
    .filter(p => p && p.isUserAdded && !p.isDeleted)
    .map(({ isEditing, isNew, ...rest }) => ({ ...rest, isUserAdded: true }));
};

export default function useQuote() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: quoteId } = useParams();
  const { getQuoteNumber, getMultipleData, createQuote, getSawImageData, getImageWithDetails, getModularConveyors, getQuoteById, updateQuoteVersion, addCustomerLocation, createSaw } = useDashboard();

  const [state, setState] = React.useReducer(stateReducer, initialState);
  
  const setByPath = (path, valueOrUpdater) => {
    setState({ type: 'SET_BY_PATH', path, value: valueOrUpdater });
  };

  // Create all aliases using the function from state.js
  const aliases = createAliases(state, setByPath);

  // Destructure all values and setters from aliases
  const {
    // UI
    ui: {
      isRotatedArrow: { value: isRotatedArrow, set: setIsRotatedArrow },
      isRotatedBox: { value: isRotatedBox, set: setIsRotatedBox },
      isReversed: { value: isReversed, set: setIsReversed },
      orientation: { value: orientation, set: setOrientation },
      isAddingNew: { value: isAddingNew, set: setIsAddingNew },
      items: { value: items, set: setItems },
    },
    // Modals
    modals: {
      customModalOpen: { value: customModalOpen, set: setCustomModalOpen },
      showQuoteDetailsModal: { value: showQuoteDetailsModal, set: setShowQuoteDetailsModal },
      showLengthLimitModal: { value: showLengthLimitModal, set: setShowLengthLimitModal },
      showSpacerModal: { value: showSpacerModal, set: setShowSpacerModal },
      lengthLimitMessage: { value: lengthLimitMessage, set: setLengthLimitMessage },
    },
    // Lists
    lists: {
      customers: { value: customers, set: setCustomers },
      saws: { value: saws, set: setSaws },
      dropdownOptions: { value: dropdownOptions, set: setDropdownOptions },
    },
    // Selections
    selections: {
      selectedSaw: { value: selectedSaw, set: setSelectedSaw },
      selectedCustomer: { value: selectedCustomer, set: setSelectedCustomer },
      selectedCustomerName: { value: selectedCustomerName, set: setSelectedCustomerName },
      selectedRollerLength: { value: selectedRollerLength, set: setSelectedRollerLength },
      leftBoxRollerLength: { value: leftBoxRollerLength, set: setLeftBoxRollerLength },
      rightBoxRollerLength: { value: rightBoxRollerLength, set: setRightBoxRollerLength },
      selectedConveyorStyle: { value: selectedConveyorStyle, set: setSelectedConveyorStyle },
      leftBoxConveyorStyle: { value: leftBoxConveyorStyle, set: setLeftBoxConveyorStyle },
      rightBoxConveyorStyle: { value: rightBoxConveyorStyle, set: setRightBoxConveyorStyle },
      additionalEmails: { value: additionalEmails, set: setAdditionalEmails },
      selectedAdditionalEmails: { value: selectedAdditionalEmails, set: setSelectedAdditionalEmails },
      selectedContact: { value: selectedContact, set: setSelectedContact },
    },
    // Boxes
    boxes: {
      leftBoxItems: { value: leftBoxItems, set: setLeftBoxItems },
      rightBoxItems: { value: rightBoxItems, set: setRightBoxItems },
      activeBox: { value: activeBox, set: setActiveBox },
      checkedItems: { value: checkedItems, set: setCheckedItems },
      flippedBoxes: { value: flippedBoxes, set: setFlippedBoxes },
      filenameFlipStates: { value: filenameFlipStates, set: setFilenameFlipStates },
      motorSelections: { value: motorSelections, set: setMotorSelections },
      taSelections: { value: taSelections, set: setTASelections },
      stkSelections: { value: stkSelections, set: setSTKSelections },
      leftBoxCacheClear: { value: leftBoxCacheClear, set: setLeftBoxCacheClear },
      rightBoxCacheClear: { value: rightBoxCacheClear, set: setRightBoxCacheClear },
    },
    // Cache
    cache: {
      globalImageCache: { value: globalImageCache, set: setGlobalImageCache },
    },
    // Lengths & Buttons
    lengthsAndButtons: {
      infeedLength: { value: infeedLength, set: setInfeedLength },
      outfeedLength: { value: outfeedLength, set: setOutfeedLength },
      buttonsEnabled: { value: buttonsEnabled, set: setButtonsEnabled },
    },
    // Form
    form: {
      formFields: { value: formFields, set: setFormFields },
    },
    // Quote
    quote: {
      isQuoteCreated: { value: isQuoteCreated, set: setIsQuoteCreated },
      quoteNumber: { value: quoteNumber, set: setQuoteNumber },
      quoteDetails: { value: quoteDetails, set: setQuoteDetails },
      brochure: { value: quoteBrochure, set: setQuoteBrochure },
    },
    // Data
    data: {
      sampleData: { value: sampleData, set: setSampleData },
    },
    // Images/details
    images: {
      motorVariantImageUrls: { value: motorVariantImageUrls, set: setMotorVariantImageUrls },
      taImageUrls: { value: taImageUrls, set: setTAImageUrls },
      stkImageUrls: { value: stkImageUrls, set: setSTKImageUrls },
      motorVariantDetails: { value: motorVariantDetails, set: setMotorVariantDetails },
      taDetails: { value: taDetails, set: setTADetails },
      stkDetails: { value: stkDetails, set: setSTKDetails },
    },
    // Motors
    motors: {
      motorVariantByBox: { value: motorVariantByBox, set: setMotorVariantByBox },
      motorDetailsByBox: { value: motorDetailsByBox, set: setMotorDetailsByBox },
      isFlipped: { value: isFlipped, set: setIsFlipped },
    },
    // STK
    stk: {
      stkVariantByPortion: { value: stkVariantByPortion, set: setSTKVariantByPortion },
    },
    // TA
    ta: {
      taVariantByPortion: { value: taVariantByPortion, set: setTAVariantByPortion },
    },
    // Media
    media: {
      sawImage: { value: sawImage, set: setSawImage },
    },
  } = aliases;

  // Cache of the four BABS-8-ATBL-30-* docs grouped by side + flip slot.
  const atblDocsRef = useRef({ leftDefault: null, leftFlipped: null, rightDefault: null, rightFlipped: null });

  // Per-box counter of expected flip-induced refetches. While > 0 for a box,
  // fetchConveyorDetailsForImages MUST NOT mutate sampleData for that box —
  // user expects "do flip" to leave quote-details rows (qty/discount/desc edits) untouched.
  const flipSuppressRef = useRef({ left: 0, right: 0 });

  const getATBLImageName = useCallback((boxName, isFlipped) => {
    const doc = pickATBLDoc(atblDocsRef.current, boxName, isFlipped);
    return doc?.image_name || '';
  }, []);

  // TA variant -> image filename (no prefix). 5FT uses TAUp/TADown; others use TA{Label}Up/TADown
  const getTAFilenamesForVariant = useCallback((variant, isDown) => {
    const v = variant || '5FT';
    if (v === '5FT') return isDown ? 'TADown.jpg' : 'TAUp.jpg';
    if (v === '10FT') return isDown ? 'TA10FTDown.jpg' : 'TA10FTUp.jpg';
    if (v === '15') return isDown ? 'TA15Down.jpg' : 'TA15Up.jpg';
    if (v === '20') return isDown ? 'TA20Down.jpg' : 'TA20Up.jpg';
    if (v === 'SPECIAL') return isDown ? 'TASpecialDown.jpg' : 'TASpecialUp.jpg';
    return isDown ? 'TADown.jpg' : 'TAUp.jpg';
  }, []);

  // Helper functions moved out of useEffects
  const getRollerPrefixForBox = useCallback((boxName) => {
    const rollerLength = boxName === 'left' ? leftBoxRollerLength : rightBoxRollerLength;
    if (!rollerLength) return '';
    const lengthNumber = rollerLength.replace('"', '');
    return lengthNumber + 'RL';
  }, [leftBoxRollerLength, rightBoxRollerLength]);

  const loadMotorVariants = useCallback(async () => {
    try {
      const leftPrefix = getRollerPrefixForBox('left');
      const rightPrefix = getRollerPrefixForBox('right');
      const leftMotorVariants = leftPrefix ? await Promise.all([
        getImageWithDetails(leftPrefix + 'motor.jpg'),
        getImageWithDetails(leftPrefix + 'motor1.jpg'),
        getImageWithDetails(leftPrefix + 'motor2.jpg')
      ]) : [null, null, null];
      const rightMotorVariants = rightPrefix && rightPrefix !== leftPrefix ? await Promise.all([
        getImageWithDetails(rightPrefix + 'motor.jpg'),
        getImageWithDetails(rightPrefix + 'motor1.jpg'),
        getImageWithDetails(rightPrefix + 'motor2.jpg')
      ]) : leftMotorVariants;
      setMotorVariantImageUrls(prev => ({
        left: { motor: leftMotorVariants[0]?.url || null, motor1: leftMotorVariants[1]?.url || null, motor2: leftMotorVariants[2]?.url || null },
        right: { motor: rightMotorVariants[0]?.url || null, motor1: rightMotorVariants[1]?.url || null, motor2: rightMotorVariants[2]?.url || null }
      }));
      setMotorVariantDetails(prev => ({
        left: { motor: leftMotorVariants[0]?.conveyorData || null, motor1: leftMotorVariants[1]?.conveyorData || null, motor2: leftMotorVariants[2]?.conveyorData || null },
        right: { motor: rightMotorVariants[0]?.conveyorData || null, motor1: rightMotorVariants[1]?.conveyorData || null, motor2: rightMotorVariants[2]?.conveyorData || null }
      }));
      setMotorDetailsByBox(prev => ({
        left: prev.left ?? (motorSelections.left != null ? leftMotorVariants[0]?.conveyorData : null),
        right: prev.right ?? (motorSelections.right != null ? rightMotorVariants[0]?.conveyorData : null)
      }));
    } catch (error) {
      console.error('Error loading motor variants:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageWithDetails, motorSelections.left, motorSelections.right, getRollerPrefixForBox]);

  const updateMotorDetailsByBox = useCallback(() => {
    setMotorDetailsByBox(prev => ({
      left: motorSelections.left != null ? motorVariantDetails.left?.[motorVariantByBox.left] || null : null,
      right: motorSelections.right != null ? motorVariantDetails.right?.[motorVariantByBox.right] || null : null
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motorVariantByBox, motorSelections, motorVariantDetails]);

  const loadTAImages = useCallback(async () => {
    try {
      const leftPrefix = getRollerPrefixForBox('left');
      const rightPrefix = getRollerPrefixForBox('right');
      const taFilenames = [
        'TAUp.jpg', 'TADown.jpg',
        'TA10FTUp.jpg', 'TA10FTDown.jpg',
        'TA15Up.jpg', 'TA15Down.jpg',
        'TA20Up.jpg', 'TA20Down.jpg',
        'TASpecialUp.jpg', 'TASpecialDown.jpg'
      ];
      const keys = ['up', 'down', 'up10FT', 'down10FT', 'up15', 'down15', 'up20', 'down20', 'upSpecial', 'downSpecial'];
      const leftTAImages = leftPrefix ? await Promise.all(taFilenames.map(f => getImageWithDetails(leftPrefix + f))) : keys.map(() => null);
      const rightTAImages = rightPrefix && rightPrefix !== leftPrefix ? await Promise.all(taFilenames.map(f => getImageWithDetails(rightPrefix + f))) : leftTAImages;
      const toSlot = (arr) => keys.reduce((o, k, i) => ({ ...o, [k]: arr[i] }), {});
      setTAImageUrls(prev => ({
        left: toSlot(leftTAImages.map((r, i) => r?.url || null)),
        right: toSlot(rightTAImages.map((r, i) => r?.url || null))
      }));
      setTADetails(prev => ({
        left: toSlot(leftTAImages.map((r, i) => r?.conveyorData || null)),
        right: toSlot(rightTAImages.map((r, i) => r?.conveyorData || null))
      }));
    } catch (error) {
      console.error('Error loading TA images:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageWithDetails, getRollerPrefixForBox]);

  const loadSTKImages = useCallback(async () => {
    try {
      // STK images are universal — same filenames regardless of roller length or conveyor style
      const stkImages = await Promise.all([
        getImageWithDetails('BABS-5-STKA-BO-HIGH.jpg'),
        getImageWithDetails('BABS-5-STKA-BO-LOW.jpg'),
        getImageWithDetails('BABS-10-STKA-BO-HIGH.jpg'),
        getImageWithDetails('BABS-10-STKA-BO-LOW.jpg'),
        getImageWithDetails('BABS-15-STKA-BO-HIGH.jpg'),
        getImageWithDetails('BABS-15-STKA-BO-LOW.jpg'),
        getImageWithDetails('BABS-20-STKA-BO-HIGH.jpg'),
        getImageWithDetails('BABS-20-STKA-BO-LOW.jpg')
      ]);
      const stkUrlData = { high: stkImages[0]?.url || null, low: stkImages[1]?.url || null, high10FT: stkImages[2]?.url || null, low10FT: stkImages[3]?.url || null, high15FT: stkImages[4]?.url || null, low15FT: stkImages[5]?.url || null, high20FT: stkImages[6]?.url || null, low20FT: stkImages[7]?.url || null };
      const stkDetData = { high: stkImages[0]?.conveyorData || null, low: stkImages[1]?.conveyorData || null, high10FT: stkImages[2]?.conveyorData || null, low10FT: stkImages[3]?.conveyorData || null, high15FT: stkImages[4]?.conveyorData || null, low15FT: stkImages[5]?.conveyorData || null, high20FT: stkImages[6]?.conveyorData || null, low20FT: stkImages[7]?.conveyorData || null };
      setSTKImageUrls({ left: stkUrlData, right: stkUrlData });
      setSTKDetails({ left: stkDetData, right: stkDetData });
    } catch (error) {
      console.error('Error loading STK images:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageWithDetails]);

  const fetchSawDetailsAndImage = useCallback(async () => {
    if (selectedSaw && selectedSaw.image_name) {
      try {
        // Determine the image name based on orientation
        let imageName = selectedSaw.image_name;
        const isStaticSaw = imageName.toLowerCase().includes('static');

        if (orientation === 'right-to-left' && !isStaticSaw) {
          // Insert "Flip" before the file extension
          const lastDotIndex = imageName.lastIndexOf('.');
          if (lastDotIndex !== -1) {
            // Split into name and extension
            const baseName = imageName.substring(0, lastDotIndex);
            const extension = imageName.substring(lastDotIndex);
            // Combine with "Flip" inserted before extension
            imageName = `${baseName}Flip${extension}`;
          } else {
            // If no extension found, just append "Flip"
            imageName = `${imageName}Flip`;
          }
        }
        
        const result = await getSawImageData(imageName);
        if (result.success) {
          setSawImage(result.url);
          if (result.sawDetails) {
            setSelectedSaw(prev => ({ ...prev, ...result.sawDetails }));
            setFormFields(prev => ({ ...prev, datumSide: result.sawDetails.datum_side || '', workBedHeight: result.sawDetails.work_bed_height?.toString() || '', sawType: result.sawDetails.saw_type || '',   price: result.sawDetails.price?.toString() || prev.price }));
          }
        } else {
          console.error('Failed to fetch saw details:', result.error);
          setSawImage(null);
        }
      } catch (error) {
        console.error('Error fetching saw details:', error);
        setSawImage(null);
      }
    } else {
      setSawImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSaw, getSawImageData, orientation]);

  const reloadImages = useCallback(async () => {
    try {
      const leftPrefix = getRollerPrefixForBox('left');
      const rightPrefix = getRollerPrefixForBox('right');
      if (leftPrefix) {
        const [m0, m1, m2] = await Promise.all([
          getImageWithDetails(leftPrefix + 'motor.jpg'),
          getImageWithDetails(leftPrefix + 'motor1.jpg'),
          getImageWithDetails(leftPrefix + 'motor2.jpg')
        ]);
        setMotorVariantImageUrls(prev => ({ ...prev, left: { motor: m0?.url || null, motor1: m1?.url || null, motor2: m2?.url || null } }));
        setMotorVariantDetails(prev => ({ ...prev, left: { motor: m0?.conveyorData || null, motor1: m1?.conveyorData || null, motor2: m2?.conveyorData || null } }));
      }
      if (rightPrefix && rightPrefix !== leftPrefix) {
        const [m0, m1, m2] = await Promise.all([
          getImageWithDetails(rightPrefix + 'motor.jpg'),
          getImageWithDetails(rightPrefix + 'motor1.jpg'),
          getImageWithDetails(rightPrefix + 'motor2.jpg')
        ]);
        setMotorVariantImageUrls(prev => ({ ...prev, right: { motor: m0?.url || null, motor1: m1?.url || null, motor2: m2?.url || null } }));
        setMotorVariantDetails(prev => ({ ...prev, right: { motor: m0?.conveyorData || null, motor1: m1?.conveyorData || null, motor2: m2?.conveyorData || null } }));
      }
      if (leftPrefix) {
        const taFilenames = ['TAUp.jpg', 'TADown.jpg', 'TA10FTUp.jpg', 'TA10FTDown.jpg', 'TA15Up.jpg', 'TA15Down.jpg', 'TA20Up.jpg', 'TA20Down.jpg', 'TASpecialUp.jpg', 'TASpecialDown.jpg'];
        const taKeys = ['up', 'down', 'up10FT', 'down10FT', 'up15', 'down15', 'up20', 'down20', 'upSpecial', 'downSpecial'];
        const leftTAResults = await Promise.all(taFilenames.map(f => getImageWithDetails(leftPrefix + f)));
        const leftTAUrls = taKeys.reduce((o, k, i) => ({ ...o, [k]: leftTAResults[i]?.url || null }), {});
        const leftTADets = taKeys.reduce((o, k, i) => ({ ...o, [k]: leftTAResults[i]?.conveyorData || null }), {});
        setTAImageUrls(prev => ({ ...prev, left: leftTAUrls }));
        setTADetails(prev => ({ ...prev, left: leftTADets }));
      }
      if (rightPrefix && rightPrefix !== leftPrefix) {
        const taFilenames = ['TAUp.jpg', 'TADown.jpg', 'TA10FTUp.jpg', 'TA10FTDown.jpg', 'TA15Up.jpg', 'TA15Down.jpg', 'TA20Up.jpg', 'TA20Down.jpg', 'TASpecialUp.jpg', 'TASpecialDown.jpg'];
        const taKeys = ['up', 'down', 'up10FT', 'down10FT', 'up15', 'down15', 'up20', 'down20', 'upSpecial', 'downSpecial'];
        const rightTAResults = await Promise.all(taFilenames.map(f => getImageWithDetails(rightPrefix + f)));
        const rightTAUrls = taKeys.reduce((o, k, i) => ({ ...o, [k]: rightTAResults[i]?.url || null }), {});
        const rightTADets = taKeys.reduce((o, k, i) => ({ ...o, [k]: rightTAResults[i]?.conveyorData || null }), {});
        setTAImageUrls(prev => ({ ...prev, right: rightTAUrls }));
        setTADetails(prev => ({ ...prev, right: rightTADets }));
      }
      // STK images are universal — reload with fixed filenames
      {
        const [stkHigh, stkLow, stkHigh10FT, stkLow10FT, stkHigh15FT, stkLow15FT, stkHigh20FT, stkLow20FT] = await Promise.all([
          getImageWithDetails('BABS-5-STKA-BO-HIGH.jpg'),
          getImageWithDetails('BABS-5-STKA-BO-LOW.jpg'),
          getImageWithDetails('BABS-10-STKA-BO-HIGH.jpg'),
          getImageWithDetails('BABS-10-STKA-BO-LOW.jpg'),
          getImageWithDetails('BABS-15-STKA-BO-HIGH.jpg'),
          getImageWithDetails('BABS-15-STKA-BO-LOW.jpg'),
          getImageWithDetails('BABS-20-STKA-BO-HIGH.jpg'),
          getImageWithDetails('BABS-20-STKA-BO-LOW.jpg')
        ]);
        const stkUrlData = { high: stkHigh?.url || null, low: stkLow?.url || null, high10FT: stkHigh10FT?.url || null, low10FT: stkLow10FT?.url || null, high15FT: stkHigh15FT?.url || null, low15FT: stkLow15FT?.url || null, high20FT: stkHigh20FT?.url || null, low20FT: stkLow20FT?.url || null };
        const stkDetData = { high: stkHigh?.conveyorData || null, low: stkLow?.conveyorData || null, high10FT: stkHigh10FT?.conveyorData || null, low10FT: stkLow10FT?.conveyorData || null, high15FT: stkHigh15FT?.conveyorData || null, low15FT: stkLow15FT?.conveyorData || null, high20FT: stkHigh20FT?.conveyorData || null, low20FT: stkLow20FT?.conveyorData || null };
        setSTKImageUrls({ left: stkUrlData, right: stkUrlData });
        setSTKDetails({ left: stkDetData, right: stkDetData });
      }
    } catch (error) {
      console.error('Error reloading images:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageWithDetails, getRollerPrefixForBox]);

  const fetchQuoteNumber = useCallback(async () => {
    if (!user?.name) return;
    const res = await getQuoteNumber(user.name);
    if (res.success) { 
      setQuoteNumber(res.data); 
    } else { 
      console.error('Error generating quote number:', res.error); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, getQuoteNumber]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const conveyorRes = await getModularConveyors();
      if (conveyorRes.success && conveyorRes.conveyors) {
        const uniqueConveyorStyles = [];
        const seenStyles = new Set();
        conveyorRes.conveyors.forEach(conveyor => {
          const style = conveyor.conveyor_style || conveyor.style;
          if (style && !seenStyles.has(style)) {
            seenStyles.add(style);
            uniqueConveyorStyles.push({ value: style, label: style });
          }
        });
        setDropdownOptions(prev => ({ ...prev, conveyorStyles: uniqueConveyorStyles }));
        atblDocsRef.current = extractATBLDocs(conveyorRes.conveyors);
      }
      const res = await getMultipleData(['customers', 'saws', 'products']);
      if (res.success) {
        setCustomers(res.data.customers || []);
        setSaws(res.data.saws || []);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMultipleData, getModularConveyors]);

  const updateRollerAndStyleSelections = useCallback(() => {
    if (activeBox === 'left') { 
      setSelectedRollerLength(leftBoxRollerLength || ''); 
    } else { 
      setSelectedRollerLength(rightBoxRollerLength || ''); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBox, leftBoxRollerLength, rightBoxRollerLength]);

  const updateConveyorStyleSelection = useCallback(() => {
    if (activeBox === 'left') { 
      setSelectedConveyorStyle(leftBoxConveyorStyle); 
    } else { 
      setSelectedConveyorStyle(rightBoxConveyorStyle); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBox, leftBoxConveyorStyle, rightBoxConveyorStyle]);

  const updateButtonsEnabled = useCallback(() => {
    const normalizedStyle = typeof selectedConveyorStyle === 'string' 
      ? selectedConveyorStyle.toUpperCase().replace(/\s+/g, '') 
      : selectedConveyorStyle?.value?.toUpperCase().replace(/\s+/g, '') || '';
    const hasRequiredSelections = normalizedStyle && selectedRollerLength;
    
    if (normalizedStyle.includes('HD') || normalizedStyle.includes('HEAVYDUTY')) {
      setButtonsEnabled({ 
        tenFt: hasRequiredSelections, 
        tenFtFork: hasRequiredSelections, 
        fiveFt: hasRequiredSelections, 
        liftTable: hasRequiredSelections, 
        spacer: hasRequiredSelections 
      });
    } else if (normalizedStyle.includes('SG') || normalizedStyle.includes('SIGMA')) {
      setButtonsEnabled({ 
        tenFt: hasRequiredSelections, 
        tenFtFork: false, 
        fiveFt: hasRequiredSelections, 
        liftTable: false, 
        spacer: hasRequiredSelections 
      });
    } else if (normalizedStyle.includes('EX') || normalizedStyle.includes('EXTREME')) {
      setButtonsEnabled({ 
        tenFt: hasRequiredSelections, 
        tenFtFork: false, 
        fiveFt: hasRequiredSelections, 
        liftTable: false, 
        spacer: hasRequiredSelections 
      });
    } else if (normalizedStyle) {
      setButtonsEnabled({ 
        tenFt: hasRequiredSelections, 
        tenFtFork: hasRequiredSelections, 
        fiveFt: hasRequiredSelections, 
        liftTable: hasRequiredSelections, 
        spacer: hasRequiredSelections 
      });
    } else {
      setButtonsEnabled({ 
        tenFt: false, 
        tenFtFork: false, 
        fiveFt: false, 
        liftTable: false, 
        spacer: false 
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConveyorStyle, selectedRollerLength]);

  const handleQuoteCreatedScroll = useCallback(() => {
    if (isQuoteCreated) {
      window.scrollTo(0, 0);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (topRef.current) { 
          topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }); 
        }
        setTimeout(() => { setIsQuoteCreated(false); }, 500);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuoteCreated]);

  // useEffects now use the extracted functions
  useEffect(() => {
    loadMotorVariants();
  }, [loadMotorVariants]);

  useEffect(() => {
    updateMotorDetailsByBox();
  }, [updateMotorDetailsByBox]);

  useEffect(() => {
    loadTAImages();
  }, [loadTAImages]);

  useEffect(() => {
    loadSTKImages();
  }, [loadSTKImages]);

  useEffect(() => {
    fetchSawDetailsAndImage();
  }, [fetchSawDetailsAndImage]);

  useEffect(() => {
    if (leftBoxRollerLength || rightBoxRollerLength) {
      reloadImages();
    } else {
      setMotorVariantImageUrls({ ...DEFAULTS.images.motorVariantImageUrls });
      setTAImageUrls({ ...DEFAULTS.images.taImageUrls });
      setSTKImageUrls({ ...DEFAULTS.images.stkImageUrls });
      setMotorVariantDetails({ ...DEFAULTS.images.motorVariantDetails });
      setTADetails({ ...DEFAULTS.images.taDetails });
      setSTKDetails({ ...DEFAULTS.images.stkDetails });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftBoxRollerLength, rightBoxRollerLength, reloadImages]);

  useEffect(() => {
    fetchQuoteNumber();
  }, [fetchQuoteNumber]);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  useEffect(() => {
    updateRollerAndStyleSelections();
  }, [updateRollerAndStyleSelections]);

  useEffect(() => {
    updateConveyorStyleSelection();
  }, [updateConveyorStyleSelection]);

  useEffect(() => {
    updateButtonsEnabled();
  }, [updateButtonsEnabled]);

  useEffect(() => {
    handleQuoteCreatedScroll();
  }, [handleQuoteCreatedScroll]);

  const filenameFlipStatesRef = useRef(filenameFlipStates);
  useEffect(() => {
    filenameFlipStatesRef.current = filenameFlipStates;
  }, [filenameFlipStates]);

  // Mirror checkedItems into a ref so callbacks scheduled via setTimeout (which
  // can fire before/after React's updater runs) always read the latest state
  // instead of a stale useCallback closure value. Critical for PW NP↔P swap:
  // handleLabelChange runs in a setTimeout and previously fell back to a stale
  // closure when the updater hadn't materialised yet, producing inverted
  // filename resolution (PW checked → NP, unchecked → P).
  const checkedItemsRef = useRef(checkedItems);
  useEffect(() => {
    checkedItemsRef.current = checkedItems;
  }, [checkedItems]);

  // Quote-load line-item replay: fetchQuoteData populates this ref with the
  // loaded quote, and a follow-up effect runs the PW/VR/CP/MT/TA/STK label
  // handlers AFTER leftBoxItems/rightBoxItems have been committed (the prior
  // setTimeout(0) strategy fired before the items propagated, so
  // handleLabelChange bailed at its empty-items guard).
  const pendingQuoteReplayRef = useRef(null);

  // Rest of the code remains the same...
  const getSpecialLabelFilename = useCallback((label, boxName, portionIndex = null) => {
    const currentFlipStates = filenameFlipStatesRef.current;
    switch (label) {
      case 'MT': {
        const prefix = getRollerPrefixForBox(boxName);
        const variant = motorVariantByBox?.[boxName] || 'motor';
        return `${prefix}${variant}.jpg`;
      }
      case 'TA': {
        const prefix = getRollerPrefixForBox(boxName);
        const taVariant = taVariantByPortion?.[boxName]?.[portionIndex] || '5FT';
        const filename = getTAFilenamesForVariant(taVariant, currentFlipStates[boxName]);
        return prefix ? `${prefix}${filename}` : filename;
      }
      case 'STK': {
        // STK images are universal — same filenames regardless of roller length or conveyor style
        const stkVariant = stkVariantByPortion?.[boxName]?.[portionIndex] || '5FT';
        const stkSize = stkVariant.replace('FT', '');
        const stkHeight = currentFlipStates[boxName] ? 'LOW' : 'HIGH';
        return `BABS-${stkSize}-STKA-BO-${stkHeight}.jpg`;
      }
      default:
        return '';
    }
  }, [getRollerPrefixForBox, motorVariantByBox, stkVariantByPortion, taVariantByPortion, getTAFilenamesForVariant]);

  // Mapping helpers for label-option API calls
  const LABEL_OPTION_LABELS = ['PW', 'VR', 'CP'];
  const LABEL_OPTION_PREFIX_MAP = { PW: 'PW', VR: 'VR', CP: 'CP' };
  const LABEL_OPTION_MODULE_MAP = { '10FT': '10FT', '10FTFORK': '10FTFORK', '5FT': '5FT', LFT: 'PowerLiftTable' };
  const LABEL_OPTION_SPECIALS = ['PW', 'VR', 'CP'];

  const buildLabelOptionImageNames = useCallback(
    (label, moduleAlt, boxName) => {
      const labelCode = LABEL_OPTION_PREFIX_MAP[label] || label;
      const moduleCode = LABEL_OPTION_MODULE_MAP[moduleAlt] || moduleAlt;
      if (!labelCode || !moduleCode) return [];

      const rollerSuffix = labelCode === 'CP' ? getRollerPrefixForBox(boxName) : '';
      const base = `${labelCode}${moduleCode}${rollerSuffix || ''}`;

      // Only request the .jpg variant (e.g. CP10FT24RL.jpg)
      return [`${base}.jpg`];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRollerPrefixForBox]
  );

  const upsertLabelOptionRow = useCallback(
    async (boxName, portionIndex, label, isChecked) => {
      const currentItems = boxName === 'left' ? leftBoxItems : rightBoxItems;
      const module = currentItems?.[portionIndex];
      if (!module) return;

      const portionKey = String(portionIndex);
      const labelCode = LABEL_OPTION_PREFIX_MAP[label] || label;
      const moduleCode = LABEL_OPTION_MODULE_MAP[module?.alt] || module?.alt;
      const optionKey = `${boxName}-${portionKey}-${labelCode}`;

      // Remove on uncheck
      if (!isChecked) {
        setSampleData((prev) => {
          return prev.map((item) => {
            // If this item has multiple labelOptionKeys, remove the one being unchecked
            if (item.labelOptionKeys && item.labelOptionKeys.includes(optionKey)) {
              const updatedKeys = item.labelOptionKeys.filter((key) => key !== optionKey);
              const updatedPortions = item.portionIndices?.filter((idx) => idx !== portionKey) || [];
              
              // If no keys left, remove the item entirely
              if (updatedKeys.length === 0) {
                return null;
              }
              
              // Decrease quantity and recalculate
              const newQty = Math.max(0, (parseFloat(item.qty) || 0) - 1);
              const newExtPrice = newQty * (item.actualPrice || 0);
              
              return {
                ...item,
                labelOptionKeys: updatedKeys,
                labelOptionKey: updatedKeys[0], // Keep first key for backward compatibility
                portionIndices: updatedPortions,
                portionIndex: updatedPortions[0] || item.portionIndex,
                qty: newQty,
                extPrice: newExtPrice
              };
            }
            // If item uses single labelOptionKey, remove it if it matches
            if (item.labelOptionKey === optionKey) {
              return null;
            }
            return item;
          }).filter(Boolean); // Remove null items
        });
        return;
      }

      const candidates = buildLabelOptionImageNames(label, module?.alt, boxName);
      let conveyorData = null;
      let usedImageName = null;

      for (const name of candidates) {
        if (!name) continue;
        try {
          const res = await getImageWithDetails(name);
          if (res?.conveyorData) {
            conveyorData = res.conveyorData;
            usedImageName = name;
            break;
          }
        } catch (error) {
          console.error(`Error fetching label option data for ${name}:`, error);
        }
      }

      if (!conveyorData) return;

      const qty = 1;
      const listPrice = Number(conveyorData.list ?? conveyorData.total ?? 0) || 0;
      const actualPrice = listPrice;
      const partName = conveyorData.part_number || `${labelCode}${moduleCode}`;
      const partNumber = conveyorData.part_number || `${labelCode}${moduleCode}`;
      const description = conveyorData.conveyor_name || conveyorData.module_description || `${labelCode} ${moduleCode}`;

      setSampleData((prev) => {
        // Remove the item with this specific optionKey first
        const filtered = prev.filter((item) => item.labelOptionKey !== optionKey);
        
        // Find existing item with same part name, special label, and box name
        const existingItemIndex = filtered.findIndex(
          (item) =>
            item.partName === partName &&
            item.specialLabel === labelCode &&
            item.boxName === boxName &&
            item.labelOptionKey // Only match other label-option items
        );

        if (existingItemIndex !== -1) {
          // Update existing item: increase quantity and recalculate prices
          const existingItem = filtered[existingItemIndex];
          const newQty = (parseFloat(existingItem.qty) || 0) + qty;
          const newExtPrice = newQty * actualPrice;
          
          // Track all portion indices that contribute to this grouped item
          const portionIndices = existingItem.portionIndices || [existingItem.portionIndex];
          if (!portionIndices.includes(portionKey)) {
            portionIndices.push(portionKey);
          }
          
          // Track all labelOptionKeys that contribute to this grouped item
          const labelOptionKeys = existingItem.labelOptionKeys || [existingItem.labelOptionKey];
          if (!labelOptionKeys.includes(optionKey)) {
            labelOptionKeys.push(optionKey);
          }

          filtered[existingItemIndex] = {
            ...existingItem,
            qty: newQty,
            extPrice: newExtPrice,
            portionIndices,
            labelOptionKeys,
            // Keep the first portionIndex for backward compatibility
            portionIndex: portionIndices[0]
          };
          
          return filtered;
        } else {
          // Create new item
        const newItem = {
          id: Date.now() + Math.random(),
          labelOptionKey: optionKey,
            labelOptionKeys: [optionKey], // Track all keys for this grouped item
            partName,
            partNumber,
            description,
          boxName,
          boxType: boxName === 'left' ? 'Infeed' : 'Outfeed',
          portionIndex: portionKey,
            portionIndices: [portionKey], // Track all portions for this grouped item
          qty,
          listPrice,
          discount: 0,
          actualPrice,
            extPrice: qty * actualPrice,
          isNew: false,
          isEditing: false,
          imageName: usedImageName,
          specialLabel: labelCode,
          moduleType: moduleCode
        };
        return [...filtered, newItem];
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildLabelOptionImageNames, getImageWithDetails, leftBoxItems, rightBoxItems, setSampleData]
  );

  const addSpecialLabelToQuote = useCallback(async (boxName, label, details = null, index) => {
    try {
      let conveyorData = details;
      if (!conveyorData) {
        const filename = getSpecialLabelFilename(label, boxName, index);
        const response = await getImageWithDetails(filename);
        conveyorData = response.conveyorData;
      }
      if (conveyorData) {
        setSampleData((prev) => {
          // For STK/TA labels, filter by boxName, specialLabel AND portionIndex
          // For other labels (MT), keep the old behavior (single entry per box)
          const isPortionScopedLabel = label === 'STK' || label === 'TA';
          let filtered;
          if (isPortionScopedLabel) {
            filtered = prev.filter((item) => !(
              item.boxName === boxName && 
              item.specialLabel === label && 
              item.portionIndex === index
            ));
          } else {
            // For MT, keep only one entry per box
            filtered = prev.filter((item) => !(item.boxName === boxName && item.specialLabel === label));
          }
          
          const newItem = {
            id: Date.now() + Math.random(),
            partName: conveyorData.part_number || `${label} Component`,
            partNumber: conveyorData.part_number || label,
            description: conveyorData.conveyor_name || `${label} Component`,
            boxName,
            boxType: boxName === 'left' ? 'Infeed' : 'Outfeed',
            portionIndex: isPortionScopedLabel ? index : 'special',
            qty: 1,
            listPrice: conveyorData.list || 0,
            discount: 0,
            actualPrice: conveyorData.list || 0,
            isNew: false,
            isEditing: false,
            imageName: getSpecialLabelFilename(label, boxName, index),
            specialLabel: label,
            motorIndex: index || 'special'
          };
          return [...filtered, newItem];
        });
      }
    } catch (error) {
      console.error(`Error fetching details for ${label} component:`, error);
    }
  }, [setSampleData, getSpecialLabelFilename, getImageWithDetails]);

  const removeSpecialLabelFromQuote = useCallback((boxName, label, portionIndex = null) => {
    setSampleData(prev => {
      const isPortionScopedLabel = (label === 'STK' || label === 'TA') && portionIndex !== null;
      if (isPortionScopedLabel) {
        const targetPortion = String(portionIndex);
        return prev.filter(item => !(
          item.boxName === boxName && 
          item.specialLabel === label && 
          String(item.portionIndex) === targetPortion
        ));
      }
      // For other labels or when no portionIndex provided, remove all instances for that box
      return prev.filter(item => !(item.boxName === boxName && item.specialLabel === label));
    });
  }, [setSampleData]);

  const handleMotorSelect = useCallback((boxName, portionIndex, variant) => {
    setMotorVariantByBox(prev => ({ ...prev, [boxName]: variant }));
    if (!variant) {
      setMotorSelections(prev => ({ ...prev, [boxName]: null }));
      setCheckedItems(prev => {
        const next = { left: new Map(prev.left), right: new Map(prev.right) };
        const map = new Map(next[boxName]);
        Array.from(map.keys()).forEach(key => { if (/-MT$/.test(key)) { map.delete(key); } });
        next[boxName] = map;
        return next;
      });
      setMotorDetailsByBox(prev => ({ ...prev, [boxName]: null }));
      removeSpecialLabelFromQuote(boxName, 'MT');
      return;
    }
    setMotorSelections(prev => ({ ...prev, [boxName]: portionIndex }));
    setCheckedItems(prev => {
      const next = { left: new Map(prev.left), right: new Map(prev.right) };
      const map = new Map(next[boxName]);
      Array.from(map.keys()).forEach(key => { if (/-MT$/.test(key)) { map.delete(key); } });
      map.set(`${portionIndex}-MT`, true);
      next[boxName] = map;
      return next;
    });
    const motorDetails = motorVariantDetails[boxName]?.[variant] || null;
    setMotorDetailsByBox(prev => ({ ...prev, [boxName]: motorDetails }));
    if (motorDetails) {
      removeSpecialLabelFromQuote(boxName, 'MT');
      addSpecialLabelToQuote(boxName, 'MT', motorDetails, portionIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motorVariantDetails, addSpecialLabelToQuote, removeSpecialLabelFromQuote]);

  const generateConveyorFilename = useCallback((selectedLabels, itemType, conveyorStyle, boxName, opts = {}) => {
    if (itemType === ATBL_ALT) {
      return getATBLImageName(boxName, !!filenameFlipStates[boxName]);
    }
    if (!conveyorStyle) return '';
    if (itemType.startsWith('SP-')) return 'SPACER.jpg';
    let prefix = '';
    const styleValue = typeof conveyorStyle === 'string' ? conveyorStyle : conveyorStyle.value || '';
    const styleUpper = styleValue.toUpperCase();
    if (styleUpper.includes('EX') || styleUpper.includes('EXTREME')) {
      prefix = 'EX';
    } else if (styleUpper.includes('SG') || styleUpper.includes('SIGMA')) {
      prefix = 'SG';
    } else if (styleUpper.includes('HD') || styleUpper.includes('HEAVY DUTY')) {
      prefix = '';
    } else {
      prefix = styleValue.charAt(0).toUpperCase();
    }
    const rollerLength = boxName === 'left' ? leftBoxRollerLength : rightBoxRollerLength;
    if (rollerLength) {
      const lengthNumber = rollerLength.replace('"', '');
      prefix = `${prefix}${lengthNumber}RL`;
    }
    const labelMapping = LABEL_MAPPING;
    let labelsPart = '';
    if (itemType === 'LFT') {
      const conveyorLabels = CONVEYOR_LABELS;
      labelsPart = conveyorLabels.map(label => (selectedLabels.includes(label) ? labelMapping[label] : `N${labelMapping[label]}`)).join('');
    } else if (styleUpper.includes('EX') || styleUpper.includes('EXTREME')) {
      labelsPart = 'PWNVRCP';
    } else if (styleUpper.includes('SG') || styleUpper.includes('SIGMA')) {
      labelsPart = 'PWNVRNCP';
    } else {
      const conveyorLabels = CONVEYOR_LABELS;
      labelsPart = conveyorLabels.map(label => (selectedLabels.includes(label) ? labelMapping[label] : `N${labelMapping[label]}`)).join('');
    }
    let modelPart = MODEL_MAP[itemType] || DEFAULT_MODEL;
    // Only 5FT changes filename on user-flip (other modules flip via CSS
    // scaleY(-1) in BoxComponent and keep a stable backend filename). The
    // suffix is appended only when the user has actually flipped the box;
    // the un-suffixed name is the canonical S3 default for 5FT. The earlier
    // "FLIP record is the canonical default" inversion broke HD-style 5FT
    // + CP because S3 has 24RLNPWNVRCP5FT.jpg but not the 5FTD variant.
    const isFiveFt = itemType === '5FT';
    let orientationSuffix = '';
    if (isFiveFt && !!filenameFlipStates[boxName]) {
      if (styleUpper.includes('EX') || styleUpper.includes('EXTREME') || styleUpper.includes('SG') || styleUpper.includes('SIGMA')) {
        orientationSuffix = 'FLIP';
      } else if (styleUpper.includes('HD') || styleUpper.includes('HEAVY DUTY')) {
        orientationSuffix = 'D';
      } else {
        orientationSuffix = 'FLIP';
      }
    }
    const finalFilename = `${prefix}${labelsPart}${modelPart}${orientationSuffix}.jpg`;
    return finalFilename;
  }, [filenameFlipStates, leftBoxRollerLength, rightBoxRollerLength, getATBLImageName]);

  const addOrUpdateConveyorItem = (conveyorData, boxName, portionIndex, isReplacement = false, knownFilename = null) => {
    if (!conveyorData || !conveyorData.part_number) return;
    // While a flip refetch for this box is in progress, skip the sampleData
    // rebuild so user edits (qty/discount/description/manualQty) survive.
    // URL caching happens in getImageForSelection independent of this path,
    // so the diagram still updates with the flipped image.
    if (flipSuppressRef.current[boxName] > 0) {
      flipSuppressRef.current[boxName] -= 1;
      return;
    }
    const currentItems = boxName === 'left' ? leftBoxItems : rightBoxItems;
    if (!currentItems || Object.keys(currentItems).length === 0) return;
    const imageNames = [];
    const filenameGroups = {};
    Object.keys(currentItems).forEach(portionIdx => {
      const item = currentItems[portionIdx];
      if (!item || item.type !== 'option') return;
      const selectedLabels = item.labels.filter(l => { const key = `${portionIdx}-${l}`; return checkedItems[boxName].get(key) === true; });
      const filename = generateConveyorFilename(selectedLabels, item.alt, boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, boxName);
      // PW must flow into the primary filename so the backend resolves the
      // powered variant (e.g. BABS-10-P-24) instead of the non-powered variant
      // (BABS-10-NP-24). VR/CP are still excluded — they remain pure add-on
      // line items via upsertLabelOptionRow and must not change the primary row.
      const primaryLabels = selectedLabels.filter(l => l !== 'VR' && l !== 'CP');
      const primaryFilename = generateConveyorFilename(primaryLabels, item.alt, boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, boxName, { swapFiveFt: false });
      // Use size-specific key for spacers so different sizes are separate line items
      const groupKey = item.alt.startsWith('SP-') ? `SPACER-${item.width || item.alt.split('-')[1]}FT.jpg` : primaryFilename;
      if (!filenameGroups[groupKey]) {
        filenameGroups[groupKey] = { portions: [], item: item, selectedLabels: selectedLabels, actualFilename: filename };
      }
      filenameGroups[groupKey].portions.push(portionIdx);
      // Fetch the primary (no PW/VR/CP) filename so the primary row resolves to
      // the unmodified module's part data — toggling VR/PW/CP must not change
      // the InFeed/OutFeed model row. Spacers always resolve to 'SPACER.jpg'.
      imageNames.push(item.alt.startsWith('SP-') ? filename : primaryFilename);
    });
    const knownDetails = {};
    if (knownFilename) { knownDetails[knownFilename] = conveyorData; }
    fetchConveyorDetailsForImages(imageNames, boxName, filenameGroups, knownDetails);
  };

const getImageForSelection = async (item, portionIndex, boxName, checkedItemsMap, isReplacement = false) => {
  if (!item || item.type !== 'option') return null;
  if (item.imageUrl) return item.imageUrl;
  const imageKey = getImageKey(item, portionIndex, boxName, checkedItemsMap);
  if (globalImageCache.has(imageKey)) {
    await fetchConveyorDataForItem(item, portionIndex, boxName, checkedItemsMap, isReplacement);
    return globalImageCache.get(imageKey);
  }
  
  // Handle spacer images
  if (item.alt.startsWith('SP-')) {
    try {
      const imageResponse = await getImageWithDetails('SPACER.jpg');
      if (imageResponse.conveyorData) {
        addOrUpdateConveyorItem(imageResponse.conveyorData, boxName, portionIndex, isReplacement);
      }
      
      if (imageResponse?.url) {
        const url = imageResponse.url;
        setGlobalImageCache(prev => new Map(prev).set(imageKey, url));
        return url;
      }
    } catch (error) {
      console.error('Error loading SPACER image:', error);
    }
  }
  
  // Handle regular conveyor images
  const selectedLabels = item.labels.filter(label => { 
    const key = `${portionIndex}-${label}`; 
    return checkedItemsMap.get(key) === true; 
  });
  const filename = generateConveyorFilename(
    selectedLabels, 
    item.alt, 
    boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, 
    boxName
  );
  
  let conveyorData = null;
  let url = null;
  
  try {
    const imageResponse = await getImageWithDetails(filename);
    conveyorData = imageResponse.conveyorData;
    url = imageResponse.url;
    
    if (conveyorData) {
      addOrUpdateConveyorItem(conveyorData, boxName, portionIndex, isReplacement, filename);
    }
  } catch (error) {
    console.error('Error loading image with generated filename:', filename, error);
  }
  
  if (url) {
    setGlobalImageCache(prev => new Map(prev).set(imageKey, url));
    return url;
  }  
  if(!url){
    return null;
  }
};

const fetchConveyorDataForItem = async (item, portionIndex, boxName, checkedItemsMap, isReplacement = false) => {
  if (item.alt.startsWith('SP-')) {
    try {
      const imageResponse = await getImageWithDetails('SPACER.jpg');
      if (imageResponse.conveyorData) {
        addOrUpdateConveyorItem(imageResponse.conveyorData, boxName, portionIndex, isReplacement);
      }
    } catch (error) { 
      console.error('Error fetching spacer conveyor data:', error);
    }
    return;
  }
  
  const selectedLabels = item.labels.filter(label => { 
    const key = `${portionIndex}-${label}`; 
    return checkedItemsMap.get(key) === true; 
  });
  const filename = generateConveyorFilename(
    selectedLabels, 
    item.alt, 
    boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, 
    boxName
  );
  
  try {
    const imageResponse = await getImageWithDetails(filename);
    if (imageResponse.conveyorData) {
      addOrUpdateConveyorItem(imageResponse.conveyorData, boxName, portionIndex, isReplacement, filename);
    }
    
  } catch (error) {
    console.error('Error fetching conveyor data:', filename, error);
  }
};

  const getImageKey = useCallback((item, portionIndex, boxName, checkedItemsMap) => {
    if (!item) return '';
    if (item.alt.startsWith('SP-')) { return `SPACER-${boxName}`; }
    const selectedLabels = item.labels.filter(label => { const key = `${portionIndex}-${label}`; return checkedItemsMap.get(key) === true; });
    const filename = generateConveyorFilename(selectedLabels, item.alt, boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, boxName);
    const imageKey = `${boxName}-${filename}`;
    return imageKey;
  }, [generateConveyorFilename, leftBoxConveyorStyle, rightBoxConveyorStyle]);

  const fetchConveyorDetailsForImages = useCallback(async (imageNames, boxName, filenameGroups, knownDetails = {}) => {
    try {
      const uniqueImageNames = [...new Set(imageNames)];
      const detailPromises = uniqueImageNames.map(async (imageName) => {
        if (knownDetails && knownDetails[imageName]) { return { imageName, conveyorData: knownDetails[imageName] }; }
        try {
          const response = await getImageWithDetails(imageName);
          return { imageName, conveyorData: response.conveyorData };
        } catch (error) {
          console.error(`Error fetching details for ${imageName}:`, error);
          return { imageName, conveyorData: null };
        }
      });
      const details = await Promise.all(detailPromises);
      setSampleData(prev => {
        // Remove existing non-special items for this box and insert fresh resolved rows
        const updated = prev.filter(item => !(item.boxName === boxName && !item.specialLabel));
        
        Object.keys(filenameGroups).forEach((groupKey) => {
          const group = filenameGroups[groupKey];
          const qty = group.portions.length;
          // Spacers store a synthetic groupKey (SPACER-NFT.jpg) and the real filename in actualFilename.
          // For everything else, groupKey IS the primary (non-label-option) filename — use it so that
          // toggling VR/PW/CP does not re-fetch a variant that overwrites the model row's part data.
          const isSpacerGroup = group.item?.alt?.startsWith('SP-');
          const lookupFilename = isSpacerGroup ? (group.actualFilename || groupKey) : groupKey;
          const detail = details.find(d => d.imageName === lookupFilename);
          const data = detail && detail.conveyorData ? detail.conveyorData : null;
          if (data?.part_number) {
            // Append feet to description for spacer items
            const isSpacer = group.item?.alt?.startsWith('SP-');
            const spacerFt = isSpacer ? (group.item.width || parseInt(group.item.alt.split('-')[1]) || 0) : 0;
            const description = isSpacer && spacerFt ? `${data?.conveyor_name || 'Spacer'} - ${spacerFt} FT` : data?.conveyor_name;

            updated.push({
              id: Date.now() + Math.random() + Math.random(),
              partName: data?.part_number ,
              partNumber: data?.part_number ,
              description: description ,
              boxName,
              boxType: boxName === 'left' ? 'Infeed' : 'Outfeed',
              portionIndex: group.portions.join(','),
              qty,
              listPrice: data?.list || 0,
              discount: 0,
              actualPrice: (data?.list || 0),
              isNew: false,
              isEditing: false,
              imageName: lookupFilename
            });
          }
        });

        return updated;
      });
    } catch (error) {
      console.error('Error fetching conveyor details:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageWithDetails, leftBoxItems, rightBoxItems, setSampleData]);


  const handleSpecialLabelChange = useCallback(async (boxName, portionIndex, label, isChecked, type) => {
    setCheckedItems(prev => {
      const newState = { left: new Map(prev.left), right: new Map(prev.right) };
      const key = `${portionIndex}-${label}`;
      newState[boxName].set(key, isChecked);
      const updatedLabelCount = Array.from(newState[boxName].entries()).filter(([k, v]) => k.endsWith(`-${label}`) && v === true).length;
      
      // For STK/TA labels, always add/remove per portion
      if (label === 'STK' || label === 'TA') {
        if (isChecked || type) {
          addSpecialLabelToQuote(boxName, label, null, portionIndex);
        } else {
          removeSpecialLabelFromQuote(boxName, label, portionIndex);
        }
      } else {
        // For other labels (MT), keep the old behavior
        if ((isChecked && updatedLabelCount === 1) || type) {
          addSpecialLabelToQuote(boxName, label, null, portionIndex);
        } else if (!isChecked && updatedLabelCount === 0) {
          removeSpecialLabelFromQuote(boxName, label);
        }
      }
      return newState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSpecialLabelToQuote, removeSpecialLabelFromQuote]);

  const handleLabelChange = useCallback((boxName, portionIndex, label, isChecked, type) => {
    const currentItems = boxName === 'left' ? leftBoxItems : rightBoxItems;
    if (!currentItems || Object.keys(currentItems).length === 0) return;
    if (SPECIAL_LABELS.includes(label)) {
      handleSpecialLabelChange(boxName, portionIndex, label, isChecked, type);
      return;
    }
    const key = `${portionIndex}-${label}`;
    // Build the new checked map BEFORE setCheckedItems so we read the new
    // value synchronously regardless of whether React runs the updater eagerly
    // or lazily. The setter callback ran lazily under React 18 batching, which
    // left a side-channel `nextCheckedState` null and forced a fallback to the
    // stale useCallback closure value of `checkedItems`.
    const latestChecked = checkedItemsRef.current;
    const newState = {
      left: new Map(latestChecked.left),
      right: new Map(latestChecked.right),
    };
    newState[boxName].set(key, isChecked);
    setCheckedItems(newState);

    // Rebuild filename groups using the updated checked items and refresh table rows
    try {
      const boxCheckedMap = newState[boxName];
      const filenameGroups = {};
      const imageNames = [];
      Object.keys(currentItems).forEach(portionIdx => {
        const item = currentItems[portionIdx];
        if (!item || item.type !== 'option') return;
        const selectedLabels = item.labels.filter(l => boxCheckedMap.get(`${portionIdx}-${l}`) === true);
        const filename = generateConveyorFilename(
          selectedLabels,
          item.alt,
          boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle,
          boxName
        );
        // PW must flow into the primary filename so the backend resolves the
        // powered variant. VR/CP are excluded so they remain pure add-on line
        // items and never overwrite the primary row.
        const primaryLabels = selectedLabels.filter(l => l !== 'VR' && l !== 'CP');
        const primaryFilename = generateConveyorFilename(
          primaryLabels,
          item.alt,
          boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle,
          boxName,
          { swapFiveFt: false }
        );
        const groupKey = item.alt.startsWith('SP-')
          ? `SPACER-${item.width || item.alt.split('-')[1]}FT.jpg`
          : primaryFilename;
        if (!filenameGroups[groupKey]) {
          filenameGroups[groupKey] = { portions: [], item, selectedLabels, actualFilename: filename };
        }
        filenameGroups[groupKey].portions.push(portionIdx);
        // Fetch the primary (no PW/VR/CP) filename so the InFeed/OutFeed model
        // row keeps its unmodified part data when VR/PW/CP toggle. Spacers
        // always resolve to 'SPACER.jpg'.
        imageNames.push(item.alt.startsWith('SP-') ? filename : primaryFilename);
      });
      fetchConveyorDetailsForImages(imageNames, boxName, filenameGroups);
    } catch (e) {
      console.error('Error updating table after label change:', e);
    }

    // New: fetch label-option specific rows for options table without replacing existing logic
    if (LABEL_OPTION_LABELS.includes(label)) {
      upsertLabelOptionRow(boxName, portionIndex, label, isChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftBoxItems, rightBoxItems, handleSpecialLabelChange, upsertLabelOptionRow]);


  const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

  const calculateTotalLength = useCallback((boxItems) => {
    return boxItems.reduce((total, item) => {
      if (item.alt === '10FT' || item.alt === '10FTFORK') return total + 10;
      if (item.alt === '5FT' || item.alt === 'LFT') return total + 5;
      if (item.alt.startsWith('SP-')) {
        const spacerValue = parseInt(item.alt.split('-')[1]);
        return total + (isNaN(spacerValue) ? 0 : spacerValue);
      }
      return total;
    }, 0);
  }, []);

  const validateLength = useCallback((length) => {
    return Math.min(Math.max(length, 0), LENGTH_LIMIT_MAX_FT);
  }, []);

  // const handleConveyorStyleSelection = (style) => {
  //   setSelectedConveyorStyle(style);
  //   if (activeBox === 'left') {
  //     setLeftBoxConveyorStyle(style);
  //     handleClearBox('left');
  //     toast.success('Infeed cleared due to conveyor style change');
  //     setLeftBoxRollerLength('');
  //     setSelectedRollerLength('');
  //   } else {
  //     setRightBoxConveyorStyle(style);
  //     handleClearBox('right');
  //     toast.success('Outfeed cleared due to conveyor style change');
  //     setRightBoxRollerLength('');
  //     setSelectedRollerLength('');
  //   }
  // };
  const handleConveyorStyleSelection = (style) => {
    setSelectedConveyorStyle(style);
    // Apply style to both boxes
    setLeftBoxConveyorStyle(style);
    setRightBoxConveyorStyle(style);
    // Clear both boxes
    handleClearBox('left');
    handleClearBox('right');
    // Reset roller lengths for both boxes
    setLeftBoxRollerLength('');
    setRightBoxRollerLength('');
    setSelectedRollerLength('');
    
    toast.success('Both feeds cleared due to conveyor style change');
  };

  const buildPrimaryContact = (customer) => {
    if (!customer) return null;
    const c = customer.customerData || customer;
    return {
      source: 'primary',
      location_id: null,
      name: c.primary_contact || '',
      title: c.title || '',
      phone: c.phone || '',
      email: c.email || '',
      location: [c.address, c.address2, c.city, c.state, c.zip].filter(Boolean).join(', ')
    };
  };

  const handleCustomerSelection = (customerName) => {
    const customer = customers.find(c => c.customer === customerName || (c.customerData && c.customerData.customer === customerName));
    setSelectedCustomer(customer);
    setSelectedCustomerName(customer?.customer || customer?.customerData?.customer || customerName);
    if (customer) {
      setFormFields((prev) => ({ ...prev, standard_discount: customer.standard_discount || '' }));

      // Default selected contact to the customer's primary contact
      setSelectedContact(buildPrimaryContact(customer));

      // Extract additional emails from customer's additional_locations only
      const additionalEmails = [];
      if (customer.additional_locations && Array.isArray(customer.additional_locations)) {
        customer.additional_locations.forEach(location => {
          if (location.email && location.email.trim()) {
            additionalEmails.push(location.email.trim());
          }
        });
      }

      setAdditionalEmails(additionalEmails);
      setSelectedAdditionalEmails([]); // Reset selected emails when customer changes
    } else {
      setSelectedContact(null);
      setAdditionalEmails([]);
      setSelectedAdditionalEmails([]);
    }
  };

  // Add a new additional_location to the currently selected customer, then
  // refresh customer list and select the new contact.
  const handleAddContact = async (locationPayload) => {
    if (!selectedCustomer?._id) {
      toast.error('No customer selected');
      return { success: false };
    }
    const res = await addCustomerLocation(selectedCustomer._id, locationPayload);
    if (!res.success) {
      toast.error(res.error || 'Failed to add contact');
      return res;
    }
    // Refresh customers and select the newly added contact
    const refreshed = await getMultipleData(['customers']);
    if (refreshed.success) {
      const list = refreshed.data.customers || [];
      setCustomers(list);
      const updated = list.find(c => c._id === selectedCustomer._id);
      if (updated) {
        setSelectedCustomer(updated);
        const newLoc = (updated.additional_locations || []).find(
          (l) => l.location === locationPayload.location && l.primary_contact === locationPayload.primary_contact
        ) || (updated.additional_locations || []).slice(-1)[0];
        if (newLoc) {
          setSelectedContact({
            source: 'additional',
            location_id: newLoc._id || null,
            name: newLoc.primary_contact || '',
            title: '',
            phone: newLoc.primary_contact_number || '',
            email: newLoc.email || '',
            location: newLoc.location || ''
          });
        }
        const refreshedEmails = (updated.additional_locations || [])
          .map(l => (l.email || '').trim())
          .filter(Boolean);
        setAdditionalEmails(refreshedEmails);
      }
    }
    toast.success('Contact added');
    return { success: true };
  };

  const handleSawSelection = (selectedOption) => {
    const selected = saws.find(saw => `${saw.brand || 'Unknown Brand'} - ${saw.model || 'Unknown Model'}` === selectedOption);
    setSelectedSaw(selected);
    setFormFields(prev => ({ ...prev, datumSide: selected?.datum_side || '', workBedHeight: selected?.work_bed_height?.toString() || '', sawType: selected?.saw_type || '',  price: selected?.price?.toString() || '' }));
  };

  // Clear the chosen saw entirely. The quote can be saved with no saw selected —
  // saw_id_1039 serialises to '' and no validation requires one. Clearing also wipes the
  // mirrored form fields (and, via the fetchSawDetailsAndImage effect, the saw image).
  const handleRemoveSaw = () => {
    setSelectedSaw(null);
    setFormFields(prev => ({ ...prev, datumSide: '', workBedHeight: '', sawType: '', price: '' }));
  };

  // Create a brand-new saw straight from the quote's saw dropdown ("Add New Saw"). Persists
  // it to the backend (POST /saws), refreshes the dropdown list so it's there forever, and
  // auto-selects it for the current quote. Throws on failure so the CreateSaw modal stays
  // open (its handleSubmit swallows the throw and skips its own onClose).
  const handleAddNewSaw = async (formData) => {
    const { image, pdf, ...rest } = formData;
    const res = await createSaw(rest, image, pdf);
    if (!res?.success) {
      toast.error(res?.error || 'Failed to add saw');
      throw new Error(res?.error || 'Failed to add saw');
    }
    // Refresh the saw list so the new saw is a permanent dropdown option.
    const listRes = await getMultipleData(['saws']);
    if (listRes?.success) setSaws(listRes.data || []);
    // Auto-select the just-created saw; the fetchSawDetailsAndImage effect fills in its image.
    const created = res.saw;
    if (created) {
      setSelectedSaw(created);
      setFormFields(prev => ({ ...prev, datumSide: created.datum_side || '', workBedHeight: created.work_bed_height?.toString() || '', sawType: created.saw_type || '', price: created.price?.toString() || '' }));
    }
    toast.success('Saw added');
  };

  const sawOptions = [...saws].sort((a, b) => {
    const aBrand = (a.brand || 'Unknown Brand').toLowerCase();
    const bBrand = (b.brand || 'Unknown Brand').toLowerCase();
    const brandCompare = aBrand.localeCompare(bBrand);
    if (brandCompare !== 0) return brandCompare;
    const aModel = (a.model || 'Unknown Model').toLowerCase();
    const bModel = (b.model || 'Unknown Model').toLowerCase();
    return aModel.localeCompare(bModel);
  }).map((saw) => `${saw.brand || 'Unknown Brand'} - ${saw.model || 'Unknown Model'}`);

  const clearQuoteDetailsForBox = (boxName) => {
    setSampleData(prev => prev.filter(item => item.boxName !== boxName));
  };

  const handleClearBox = (boxName) => {
    if (boxName === 'left') {
      setLeftBoxItems([]);
      setCheckedItems(prev => ({ ...prev, left: new Map() }));
      setInfeedLength(0);
      setMotorSelections(prev => ({ ...prev, left: null }));
      setTASelections(prev => ({ ...prev, left: {} }));
      setSTKSelections(prev => ({ ...prev, left: {} }));
      setSTKVariantByPortion(prev => ({ ...prev, left: {} }));
      setTAVariantByPortion(prev => ({ ...prev, left: {} }));
    } else {
      setRightBoxItems([]);
      setCheckedItems(prev => ({ ...prev, right: new Map() }));
      setOutfeedLength(0);
      setMotorSelections(prev => ({ ...prev, right: null }));
      setTASelections(prev => ({ ...prev, right: {} }));
      setSTKSelections(prev => ({ ...prev, right: {} }));
      setSTKVariantByPortion(prev => ({ ...prev, right: {} }));
      setTAVariantByPortion(prev => ({ ...prev, right: {} }));
    }
    clearQuoteDetailsForBox(boxName);
    setGlobalImageCache(prev => {
      const newCache = new Map(prev);
      for (const key of Array.from(newCache.keys())) { if (key.startsWith(`${boxName}-`)) newCache.delete(key); }
      return newCache;
    });
    if (boxName === 'left') { setLeftBoxCacheClear(prev => prev + 1); } else { setRightBoxCacheClear(prev => prev + 1); }
  };
  
  // OLD VERSION — left in place for reference. Replaced because clearing the
  // image cache cascades through BoxComponent → getImageForSelection →
  // addOrUpdateConveyorItem → fetchConveyorDetailsForImages, which still wipes
  // and rebuilds sampleData rows for the box, losing user edits (qty/discount/
  // description/manualQty/custom prices) on flip.
  // const handleVerticalFlip = async (boxName) => {
  //   // Visual-only flip: toggle the orientation state and drop the image cache
  //   // so the diagram repaints with flipped images. Quote line items
  //   // (sampleData) are intentionally left untouched so user edits — qty,
  //   // manualQty overrides, custom prices/discounts/descriptions — survive.
  //   setGlobalImageCache(new Map());
  //
  //   const newFlipState = !filenameFlipStatesRef.current[boxName];
  //   setFlippedBoxes(prev => ({ ...prev, [boxName]: newFlipState }));
  //   setFilenameFlipStates(prev => ({ ...prev, [boxName]: newFlipState }));
  //   filenameFlipStatesRef.current = { ...filenameFlipStatesRef.current, [boxName]: newFlipState };
  // };

  // PRIOR VERSION — left in place for reference. Replaced because:
  //   1. setGlobalImageCache(new Map()) wiped BOTH boxes' URL caches, which
  //      caused the un-flipped box's BoxComponent to also refetch its items.
  //      Those refetches called addOrUpdateConveyorItem on the un-flipped box
  //      with a counter of 0 → not suppressed → rebuilt that box's sampleData
  //      and silently destroyed user edits on the side that wasn't flipped.
  //   2. flipSuppressRef.current[boxName] = optionCount overwrote any
  //      still-pending count from a prior rapid flip, letting a portion of
  //      in-flight refetches fall through and rebuild rows.
  // const handleVerticalFlip = async (boxName) => {
  //   // Visual-only flip: count how many option items will refetch so we can
  //   // suppress that exact number of sampleData rebuilds, then toggle state.
  //   // The image cache is cleared so the diagram repaints with the flipped images.
  //   const items = boxName === 'left' ? leftBoxItems : rightBoxItems;
  //   const optionCount = Array.isArray(items)
  //     ? items.filter(it => it && it.type === 'option').length
  //     : Object.keys(items || {}).filter(k => items[k] && items[k].type === 'option').length;
  //   flipSuppressRef.current[boxName] = optionCount;
  //
  //   setGlobalImageCache(new Map());
  //
  //   const newFlipState = !filenameFlipStatesRef.current[boxName];
  //   setFlippedBoxes(prev => ({ ...prev, [boxName]: newFlipState }));
  //   setFilenameFlipStates(prev => ({ ...prev, [boxName]: newFlipState }));
  //   filenameFlipStatesRef.current = { ...filenameFlipStatesRef.current, [boxName]: newFlipState };
  //
  //   // Safety: clear stale suppression after 5s if some fetches never resolve
  //   // (e.g., network failure). Without this a stuck counter would block legitimate
  //   // sampleData updates indefinitely.
  //   setTimeout(() => { flipSuppressRef.current[boxName] = 0; }, 5000);
  // };

  const handleVerticalFlip = async (boxName) => {
    // Visual-only flip. Only 5FT swaps to a FLIP variant filename and ATBL
    // swaps to a different doc; every other module (10FT, LFT, spacers) keeps
    // the same backend filename and flips via CSS scaleY(-1) in BoxComponent.
    // Do NOT blanket-clear the box's image cache — that forces a refetch for
    // items whose key never changed, and BoxComponent's loadImages fallback
    // can decrement the sampleData row when a URL fetch returns null, which
    // would mutate quote-details even though addOrUpdateConveyorItem is
    // suppressed. Items that DO swap filename (5FT/ATBL) miss the cache
    // naturally on the new key; suppress addOrUpdateConveyorItem for exactly
    // that count.
    const items = boxName === 'left' ? leftBoxItems : rightBoxItems;
    const itemsArr = Array.isArray(items) ? items : Object.values(items || {});
    const refetchingCount = itemsArr.filter(it =>
      it && it.type === 'option' && (it.alt === '5FT' || it.alt === ATBL_ALT)
    ).length;
    flipSuppressRef.current[boxName] += refetchingCount;

    const newFlipState = !filenameFlipStatesRef.current[boxName];
    setFlippedBoxes(prev => ({ ...prev, [boxName]: newFlipState }));
    setFilenameFlipStates(prev => ({ ...prev, [boxName]: newFlipState }));
    filenameFlipStatesRef.current = { ...filenameFlipStatesRef.current, [boxName]: newFlipState };

    // Safety: clear stale suppression after 5s if some fetches never resolve
    // (e.g., network failure). Without this a stuck counter would block legitimate
    // sampleData updates indefinitely.
    setTimeout(() => { flipSuppressRef.current[boxName] = 0; }, 5000);
  };

  const getIsExtremeDuty = (boxName) => {
    const styleValue = boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle;
    const styleStr = (typeof styleValue === 'string' ? styleValue : styleValue?.value || '').toUpperCase();
    return styleStr.includes('EX') || styleStr.includes('EXTREME');
  };

  // Check the PW (Power Walk) option for a portion if it isn't already, keeping the
  // primary filename (NP↔P swap) and the PW label-option row in sync via handleLabelChange.
  // Used to mirror MT→PW: turning Motor on auto-turns Power Walk on. The guard is essential —
  // it stops handleLabelChange from double-swapping the filename when PW is already on.
  const ensurePWChecked = (boxName, portionIndex) => {
    const key = `${portionIndex}-PW`;
    if (checkedItems[boxName].get(key)) return;
    setCheckedItems(prev => {
      const newState = { left: new Map(prev.left), right: new Map(prev.right) };
      newState[boxName].set(key, true);
      return newState;
    });
    setTimeout(() => { handleLabelChange(boxName, portionIndex, 'PW', true); }, 0);
  };

  const handleMTCheckboxChange = (boxName, portionIndex, isChecked, opts = {}) => {
    // Determine conveyor style to check if Heavy Duty (auto 5hp) vs Extreme Duty (dropdown)
    const isExtremeDuty = getIsExtremeDuty(boxName);

    setMotorSelections(prev => {
      const newSelections = { ...prev };
      if (isChecked) {
        newSelections[boxName] = portionIndex;
        setCheckedItems(prevChecked => {
          const newChecked = { left: new Map(prevChecked.left), right: new Map(prevChecked.right) };
          newChecked[boxName].set(`${portionIndex}-MT`, true);
          for (const [key] of newChecked[boxName]) { if (key.endsWith('-MT') && key !== `${portionIndex}-MT`) { newChecked[boxName].set(key, false); } }
          return newChecked;
        });
        // For Heavy Duty (non-Extreme), auto-select 5hp motor
        if (!isExtremeDuty) {
          setMotorVariantByBox(prev => ({ ...prev, [boxName]: 'motor' }));
        }
      } else {
        if (newSelections[boxName] === portionIndex) {
          newSelections[boxName] = null;
          setCheckedItems(prevChecked => {
            const newChecked = { left: new Map(prevChecked.left), right: new Map(prevChecked.right) };
            newChecked[boxName].set(`${portionIndex}-MT`, false);
            return newChecked;
          });
          // Clear motor variant on uncheck
          setMotorVariantByBox(prev => ({ ...prev, [boxName]: '' }));
        }
      }
      return newSelections;
    });
    // Deterministically add/remove the priced MT line item here, using the same
    // authoritative motor price (motorVariantDetails) that handleMotorSelect uses.
    // Previously the price row was added only through the async setTimeout →
    // handleLabelChange('MT') → addSpecialLabelToQuote(null) chain below. That chain
    // is fine when MT is toggled alone, but when PW auto-checks MT (both fire in the
    // same tick) it races PW's own handleLabelChange (which runs an absolute
    // setCheckedItems overwrite + async row rebuild), and the MT price row could be
    // dropped — the checkbox stayed checked while the motor's price never landed in
    // the quote, so the total came out short. Adding the row here, synchronously and
    // from explicit details, makes it race-proof. Heavy Duty is the auto-5hp checkbox
    // ('motor'); Extreme Duty keeps its own HP dropdown (handleMotorSelect) and is
    // never auto-checked, so we only handle the Heavy-Duty variant.
    if (!isExtremeDuty) {
      if (isChecked) {
        const motorDetails = motorVariantDetails[boxName]?.['motor'] || null;
        if (motorDetails) {
          setMotorDetailsByBox(prev => ({ ...prev, [boxName]: motorDetails }));
          removeSpecialLabelFromQuote(boxName, 'MT');
          addSpecialLabelToQuote(boxName, 'MT', motorDetails, portionIndex);
        }
        // If motorDetails isn't loaded yet, fall through to the async chain below
        // (unchanged legacy behavior) rather than regressing.
      } else {
        removeSpecialLabelFromQuote(boxName, 'MT');
        setMotorDetailsByBox(prev => ({ ...prev, [boxName]: null }));
      }
    }
    setTimeout(() => { handleLabelChange(boxName, portionIndex, 'MT', isChecked); }, 0);
    // MT ⇄ PW: turning Motor on auto-turns Power Walk on. `opts.syncPW === false` is passed
    // when this call was triggered by a PW click, to avoid a PW→MT→PW cascade.
    if (isChecked && opts.syncPW !== false) {
      ensurePWChecked(boxName, portionIndex);
    }
  };

  const handleTACheckboxChange = (boxName, portionIndex, isChecked, imageUrl) => {
    setTASelections(prev => {
      const newSelections = { ...prev };
      if (isChecked) { newSelections[boxName] = { ...newSelections[boxName], [portionIndex]: imageUrl }; } else { const updatedSelections = { ...newSelections[boxName] }; delete updatedSelections[portionIndex]; newSelections[boxName] = updatedSelections; }
      return newSelections;
    });
    setCheckedItems(prevChecked => { const newChecked = { left: new Map(prevChecked.left), right: new Map(prevChecked.right) }; const key = `${portionIndex}-TA`; newChecked[boxName].set(key, isChecked); return newChecked; });
    setTimeout(() => { handleLabelChange(boxName, portionIndex, 'TA', isChecked); }, 0);
  };

  const handleSTKCheckboxChange = (boxName, portionIndex, isChecked, imageUrl) => {
    setSTKSelections(prev => {
      const newSelections = { ...prev };
      if (isChecked) { newSelections[boxName] = { ...newSelections[boxName], [portionIndex]: imageUrl }; } else { const updatedSelections = { ...newSelections[boxName] }; delete updatedSelections[portionIndex]; newSelections[boxName] = updatedSelections; }
      return newSelections;
    });
    setCheckedItems(prevChecked => { const newChecked = { left: new Map(prevChecked.left), right: new Map(prevChecked.right) }; const key = `${portionIndex}-STK`; newChecked[boxName].set(key, isChecked); return newChecked; });
    setTimeout(() => { handleLabelChange(boxName, portionIndex, 'STK', isChecked); }, 0);
  };

  const handleCheckboxChange = (boxName, portionIndex, label) => {
    if (label === 'MT') {
      const key = `${portionIndex}-${label}`; const currentValue = checkedItems[boxName].get(key) || false; handleMTCheckboxChange(boxName, portionIndex, !currentValue);
      return;
    }
    if (label === 'TA') {
      const key = `${portionIndex}-${label}`; const currentValue = checkedItems[boxName].get(key) || false; handleTACheckboxChange(boxName, portionIndex, !currentValue); return;
    }
    if (label === 'STK') {
      const key = `${portionIndex}-${label}`; const currentValue = checkedItems[boxName].get(key) || false; handleSTKCheckboxChange(boxName, portionIndex, !currentValue); return;
    }
    const key = `${portionIndex}-${label}`;
    const currentValue = checkedItems[boxName].get(key) || false;
    const newValue = !currentValue;
    setCheckedItems(prev => {
      const newState = { left: new Map(prev.left), right: new Map(prev.right) };
      newState[boxName].set(key, newValue);
      return newState;
    });

    // PW: refresh primary row (NP↔P swap) AND add the PW label-option row.
    // handleLabelChange does both — primary filename rebuild + upsertLabelOptionRow.
    // VR/CP: only add the label-option row; the primary stays untouched.
    if (label === 'PW') {
      setTimeout(() => { handleLabelChange(boxName, portionIndex, 'PW', newValue); }, 0);
      // PW ⇄ MT: turning Power Walk on auto-turns Motor on. Heavy-Duty MT is a checkbox
      // (auto 5hp) we can mirror; Extreme-Duty MT is an HP dropdown whose value we can't
      // presume, so we leave that for the user. syncPW:false prevents a PW→MT→PW cascade.
      if (newValue && !getIsExtremeDuty(boxName) && !checkedItems[boxName].get(`${portionIndex}-MT`)) {
        handleMTCheckboxChange(boxName, portionIndex, true, { syncPW: false });
      }
    } else if (LABEL_OPTION_LABELS.includes(label)) {
      setTimeout(() => {
        upsertLabelOptionRow(boxName, portionIndex, label, newValue);
      }, 0);
    }
  };

  const handleBoxItemsChange = (boxName, newItems) => {
    if (boxName === 'left') {
      setLeftBoxItems(newItems);
      const totalLength = calculateTotalLength(newItems);
      setInfeedLength(validateLength(totalLength));
    } else {
      setRightBoxItems(newItems);
      const totalLength = calculateTotalLength(newItems);
      setOutfeedLength(validateLength(totalLength));
    }
  };

  const handleCheckedItemsChange = (boxName, newCheckedItems) => {
    setCheckedItems(prev => ({ ...prev, [boxName]: newCheckedItems }));
  };

const handleDeletePortion = useCallback((boxName, portionIndex) => {
  const indexNum = parseInt(portionIndex, 10);
  const isLeft = boxName === 'left';
  const currentItems = isLeft ? leftBoxItems : rightBoxItems;
  if (!currentItems || indexNum < 0 || indexNum >= currentItems.length) return;

  // Remove the module at index
  const newItems = currentItems.filter((_, i) => i !== indexNum);
  if (isLeft) { 
    setLeftBoxItems(newItems); 
  } else { 
    setRightBoxItems(newItems); 
  }

  // Create a new checked items map with updated indices
  const newCheckedItemsMap = new Map();
  const currentCheckedItems = checkedItems[boxName];
  
  // Process all existing checked items
  for (const [key, value] of currentCheckedItems) {
    const [keyIndex, label] = key.split('-');
    const keyIndexNum = parseInt(keyIndex, 10);
    
    // Skip items from the deleted portion
    if (keyIndexNum === indexNum) continue;
    
    // Update indices for portions after the deleted one
    if (keyIndexNum > indexNum) {
      const newKey = `${keyIndexNum - 1}-${label}`;
      newCheckedItemsMap.set(newKey, value);
    } else {
      // Keep items from portions before the deleted one
      newCheckedItemsMap.set(key, value);
    }
  }

  const mtCount = Array.from(newCheckedItemsMap.entries())
    .filter(([k, v]) => /-MT$/.test(k) && v === true).length;
  
  const noMT = mtCount === 0;

  // Update checked items state
  setCheckedItems(prev => ({
    ...prev,
    [boxName]: newCheckedItemsMap
  }));

  // Update motor selections with new indices
  setMotorSelections(prev => {
    const newSelections = { ...prev };
    const currentSelection = newSelections[boxName];
    
    if (currentSelection !== null) {
      const selectionIndex = parseInt(currentSelection, 10);
      if (selectionIndex === indexNum) {
        // Remove selection if it was the deleted portion
        newSelections[boxName] = null;
      } else if (selectionIndex > indexNum) {
        // Update index if it was after the deleted portion
        newSelections[boxName] = (selectionIndex - 1).toString();
      }
    }
    
    return newSelections;
  });

  // Update TA selections with new indices
  setTASelections(prev => {
    const newSelections = { ...prev };
    const boxSelections = { ...(newSelections[boxName] || {}) };
    const updatedBoxSelections = {};
    
    Object.keys(boxSelections).forEach(key => {
      const keyIndex = parseInt(key, 10);
      if (keyIndex === indexNum) {
        // Skip the deleted portion
        return;
      }
      
      if (keyIndex > indexNum) {
        // Update index for portions after the deleted one
        updatedBoxSelections[keyIndex - 1] = boxSelections[key];
      } else {
        // Keep portions before the deleted one
        updatedBoxSelections[keyIndex] = boxSelections[key];
      }
    });
    
    newSelections[boxName] = updatedBoxSelections;
    return newSelections;
  });

  // Update STK selections with new indices
  setSTKSelections(prev => {
    const newSelections = { ...prev };
    const boxSelections = { ...(newSelections[boxName] || {}) };
    const updatedBoxSelections = {};
    
    Object.keys(boxSelections).forEach(key => {
      const keyIndex = parseInt(key, 10);
      if (keyIndex === indexNum) {
        // Skip the deleted portion
        return;
      }
      
      if (keyIndex > indexNum) {
        // Update index for portions after the deleted one
        updatedBoxSelections[keyIndex - 1] = boxSelections[key];
      } else {
        // Keep portions before the deleted one
        updatedBoxSelections[keyIndex] = boxSelections[key];
      }
    });
    
    newSelections[boxName] = updatedBoxSelections;
    return newSelections;
  });

  // Update sampleData
  setSampleData(prev => {
    return prev.filter(item => {
      // Remove non-special items that are entirely for this portion
      if (item.boxName === boxName && !item.specialLabel) {
        if (!item.portionIndex) return true;
        const parts = String(item.portionIndex).split(',');
        const remaining = parts.filter(p => p !== String(portionIndex));
        
        // Remove if no portions remain
        if (remaining.length === 0) return false;
        
        // Adjust quantity and portionIndex if needed
        if (remaining.length !== parts.length) {
          item.qty = remaining.length;
          item.portionIndex = remaining.join(',');
        }
        return true;
      }
      
      // Handle special items
      if (item.boxName === boxName && item.specialLabel) {
        // Remove newly added label-option rows when their portion is deleted
        if (LABEL_OPTION_SPECIALS.includes(item.specialLabel)) {
          const itemPortionIndex = String(item.portionIndex);
          const deletingPortionStr = String(portionIndex);

          if (itemPortionIndex === deletingPortionStr) {
            return false;
          }

          const itemPortionNum = parseInt(itemPortionIndex, 10);
          const deletingPortionNum = parseInt(deletingPortionStr, 10);
          if (!isNaN(itemPortionNum) && !isNaN(deletingPortionNum) && itemPortionNum > deletingPortionNum) {
            item.portionIndex = itemPortionNum - 1;
          }

          return true;
        }

        // For STK/TA items, check if this specific portion is being deleted
        if (item.specialLabel === 'STK' || item.specialLabel === 'TA') {
          const itemPortionIndex = String(item.portionIndex);
          const deletingPortionStr = String(portionIndex);
          
          // Remove if this special option belongs to the deleted portion
          if (itemPortionIndex === deletingPortionStr) {
            return false;
          }
          
          // Update portionIndex if it's after the deleted portion
          const itemPortionNum = parseInt(itemPortionIndex, 10);
          const deletingPortionNum = parseInt(deletingPortionStr, 10);
          if (!isNaN(itemPortionNum) && !isNaN(deletingPortionNum) && itemPortionNum > deletingPortionNum) {
            item.portionIndex = itemPortionNum - 1;
          }
          
          return true;
        }
        
        // For other special items (MT), use the count-based approach
        if (item.specialLabel === 'MT' && noMT) return false;
        return true;
      }
      
      // Keep all other items
      return true;
    });
  });

  // Clear cached images for this box
  setGlobalImageCache(prev => {
    const copy = new Map(prev);
    for (const key of Array.from(copy.keys())) {
      if (key.startsWith(`${boxName}-`)) copy.delete(key);
    }
    return copy;
  });

  // Recompute lengths
  const totalLength = calculateTotalLength(newItems);
  if (isLeft) { 
    setInfeedLength(validateLength(totalLength)); 
  } else { 
    setOutfeedLength(validateLength(totalLength)); 
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [leftBoxItems, rightBoxItems, setLeftBoxItems, setRightBoxItems, checkedItems, setCheckedItems, setMotorSelections, setTASelections, setSTKSelections, setSampleData, setGlobalImageCache, calculateTotalLength, setInfeedLength, setOutfeedLength, validateLength]);
  
const handleArrowClick = () => {
    setIsRotatedArrow(!isRotatedArrow);
    const newOrientation = orientation === 'left-to-right' ? 'right-to-left' : 'left-to-right';
    setOrientation(newOrientation);
    
    // Update items array based on orientation
    if (newOrientation === 'right-to-left') {
      setItems(['Outfeed', 'Material Flow', 'Infeed']);
    } else {
      setItems(['Infeed', 'Material Flow', 'Outfeed']);
    }
    
    setSampleData(prev => {
      return prev.map(item => {
        if (item.boxName === 'left') { return { ...item, boxName: 'right', boxType: 'Outfeed' }; }
        else if (item.boxName === 'right') { return { ...item, boxName: 'left', boxType: 'Infeed' }; }
        return item;
      });
    });
    
    const tempItems = [...leftBoxItems];
    setLeftBoxItems([...rightBoxItems]);
    setRightBoxItems(tempItems);
    setCheckedItems((prev) => ({ left: new Map(prev.right), right: new Map(prev.left) }));
    setFlippedBoxes({ left: flippedBoxes.right, right: flippedBoxes.left });
    setFilenameFlipStates({ left: filenameFlipStates.right, right: filenameFlipStates.left });
    const tempStyle = leftBoxConveyorStyle;
    setLeftBoxConveyorStyle(rightBoxConveyorStyle);
    setRightBoxConveyorStyle(tempStyle);
    const tempRL = leftBoxRollerLength;
    setLeftBoxRollerLength(rightBoxRollerLength);
    setRightBoxRollerLength(tempRL);
    setSelectedRollerLength(prev => (activeBox === 'left' ? rightBoxRollerLength : leftBoxRollerLength));
    setMotorSelections((prev) => ({ left: prev.right, right: prev.left }));
    setMotorVariantByBox((prev) => ({ left: prev.right, right: prev.left }));
    setMotorDetailsByBox((prev) => ({ left: prev.right, right: prev.left }));
    setTASelections((prev) => ({ left: prev.right, right: prev.left }));
    setSTKSelections((prev) => ({ left: prev.right, right: prev.left }));
    setSTKVariantByPortion((prev) => ({ left: prev.right, right: prev.left }));
    setTAVariantByPortion((prev) => ({ left: prev.right, right: prev.left }));
    const tempLength = infeedLength;
    setInfeedLength(outfeedLength);
    setOutfeedLength(tempLength);
    setGlobalImageCache(new Map());
  };

  const addOptionWithImage = useCallback((imgSrc, imgAlt) => {
    const boxSetter = isRotatedBox ? setRightBoxItems : setLeftBoxItems;
    const lengthSetter = isRotatedBox ? setOutfeedLength : setInfeedLength;
    const currentItems = isRotatedBox ? rightBoxItems : leftBoxItems;
    const currentLength = calculateTotalLength(currentItems);
    let itemLength = 0;
    if (imgAlt === '10FT' || imgAlt === '10FTFORK') itemLength = 10;
    else if (imgAlt === '5FT' || imgAlt === 'LFT') itemLength = 5;
    else if (imgAlt.startsWith('SP-')) itemLength = parseInt(imgAlt.split('-')[1]) || 0;
    if (currentLength + itemLength > LENGTH_LIMIT_MAX_FT) {
      const remaining = LENGTH_LIMIT_MAX_FT - currentLength;
      setLengthLimitMessage(<>Cannot add {itemLength}ft item. Maximum length is {LENGTH_LIMIT_MAX_FT}ft.<br />Only <span className="text-red-600 font-bold">{remaining}</span>ft remaining.</>);
      setShowLengthLimitModal(true);
      return;
    }
    // Angled tables expose no PW/VR/CP/MT/STK/TA toggles — give them an empty labels array
    // so the picker doesn't render the sub-option checkboxes.
    const labelArray = imgAlt === ATBL_ALT ? [] : ALL_ITEM_LABELS;
    const newItem = { type: 'option', src: imgSrc, alt: imgAlt, labels: labelArray };
    boxSetter((prev) => {
      const newItems = [...prev, newItem];
      const totalLength = calculateTotalLength(newItems);
      lengthSetter(validateLength(totalLength));
      return newItems;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRotatedBox, rightBoxItems, leftBoxItems, calculateTotalLength, validateLength]);

  const handleLiftTable = useCallback(() => addOptionWithImage(liftTable, 'LFT'), [addOptionWithImage]);
  const handle5FtModel = useCallback(() => addOptionWithImage(fiveFtModel, '5FT'), [addOptionWithImage]);
  const handle10FtModel = useCallback(() => addOptionWithImage(tenFtModel, '10FT'), [addOptionWithImage]);
  const handle10FtForkModel = useCallback(() => addOptionWithImage(tenFtForkModel, '10FTFORK'), [addOptionWithImage]);
  const handleAngledTable = useCallback(() => {
    const targetBox = isRotatedBox ? 'right' : 'left';
    const isFlipped = !!filenameFlipStates[targetBox];
    const doc = pickATBLDoc(atblDocsRef.current, targetBox, isFlipped);
    if (!doc) {
      toast.error('Angled Table assets are not available — try again in a moment.');
      return;
    }
    addOptionWithImage(null, ATBL_ALT);
  }, [addOptionWithImage, isRotatedBox, filenameFlipStates]);

  const topRef = useRef(null);

  const resetCreateForm = () => {
    setIsQuoteCreated(true);
    setTimeout(() => {
      setIsRotatedArrow(DEFAULTS.ui.isRotatedArrow);
      setIsRotatedBox(DEFAULTS.ui.isRotatedBox);
      setIsReversed(DEFAULTS.ui.isReversed);
      setOrientation(DEFAULTS.ui.orientation);
      setItems(DEFAULTS.ui.items);
      setSelectedCustomer(DEFAULTS.selections.selectedCustomer);
      setSelectedCustomerName(DEFAULTS.selections.selectedCustomerName);
      setSelectedContact(DEFAULTS.selections.selectedContact);
      setSelectedSaw(DEFAULTS.selections.selectedSaw);
      setSelectedRollerLength(DEFAULTS.selections.selectedRollerLength);
      setSelectedConveyorStyle(DEFAULTS.selections.selectedConveyorStyle);
      setLeftBoxItems(DEFAULTS.boxes.leftBoxItems);
      setRightBoxItems(DEFAULTS.boxes.rightBoxItems);
      setCheckedItems(DEFAULTS.boxes.checkedItems);
      setFlippedBoxes(DEFAULTS.boxes.flippedBoxes);
      setInfeedLength(DEFAULTS.lengthsAndButtons.infeedLength);
      setOutfeedLength(DEFAULTS.lengthsAndButtons.outfeedLength);
      setButtonsEnabled(DEFAULTS.lengthsAndButtons.buttonsEnabled);
      setFormFields(DEFAULTS.form.formFields);
      setQuoteDetails({ ...DEFAULTS.quote.quoteDetails });
      setQuoteBrochure(DEFAULTS.quote.brochure);
      setSampleData(DEFAULTS.data.sampleData);
      setMotorSelections({ left: null, right: null });
      setMotorVariantByBox({ left: '', right: '' });
      setMotorDetailsByBox({ left: null, right: null });
      setTASelections({ left: {}, right: {} });
      setSTKSelections({ left: {}, right: {} });
      setTAVariantByPortion({ left: {}, right: {} });
      setGlobalImageCache(new Map());
      getQuoteNumber(user.name).then(res => { if (res.success) { setQuoteNumber(res.data); } else { console.error('Error generating new quote number:', res.error); } });
    }, 800);
  };

  const handleCreateQuoteButton = async (onSuccess) => {
    if (!selectedCustomer) { toast.error('Please select a customer first'); return { success: false }; }
    if (!quoteNumber) { toast.error('Quote number is missing'); return { success: false }; }
    try {
      const customerName = selectedCustomer.customer || selectedCustomer.customerData?.customer || 'Unknown Customer';
      const leftCheckedItems = checkedItems.left;
      const rightCheckedItems = checkedItems.right;
      const generateModuleData = (items, checkedItemsMap, conveyorStyle, rollerLength, boxName) => {
        return items.map((item, index) => {
          const selectedLabels = item.labels.filter(label => { const key = `${index}-${label}`; return checkedItemsMap.get(key) === true; });
          const filename = generateConveyorFilename(selectedLabels, item.alt, conveyorStyle, boxName);
          let motorVariant = '';
          if (motorSelections[boxName] === index.toString()) { motorVariant = motorVariantByBox[boxName] || ''; }
          
          // Check for special selections
          const hasMotor = checkedItemsMap.get(`${index}-MT`) || false;
          const hasVerticalRoller = checkedItemsMap.get(`${index}-VR`) || false;
          const hasCoolantPan = checkedItemsMap.get(`${index}-CP`) || false;
          
          // Get motor HP value if motor is selected
          let motorHpValue = '';
          if (hasMotor && motorSelections[boxName] === index.toString()) {
            const motorVariantType = motorVariantByBox[boxName];
            if (motorVariantType === 'motor') motorHpValue = '5HP';
            else if (motorVariantType === 'motor1') motorHpValue = '3HP';
            else if (motorVariantType === 'motor2') motorHpValue = '7.5HP';
          }

          // Get STK variant if STK is selected
          const hasSTK = checkedItemsMap.get(`${index}-STK`) || false;
          const stkVariant = hasSTK ? (stkVariantByPortion?.[boxName]?.[index] || '') : '';
          // Get TA variant if TA is selected
          const hasTA = checkedItemsMap.get(`${index}-TA`) || false;
          const taVariant = hasTA ? (taVariantByPortion?.[boxName]?.[index] || '') : '';

          // Get spacer width if this is a spacer
          const isSpacer = item.alt.startsWith('SP-');
          const spacerWidth = isSpacer ? (item.width || calculateModuleLength(item.alt) || 0) : undefined;
          
          return { 
            module_type: item.alt, 
            module_length: calculateModuleLength(item.alt), 
            module_width: spacerWidth,
            style: conveyorStyle, 
            module_roller_length: rollerLength, 
            filename: filename, 
            flips: !!flippedBoxes[boxName], 
            motor_yn: hasMotor ? 'YES' : 'NO',
            motor_hp_value: motorHpValue,
            vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO',
            coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO',
            module_options: [{ pw: checkedItemsMap.get(`${index}-PW`) || false, vr: checkedItemsMap.get(`${index}-VR`) || false, mt: motorVariant, cp: checkedItemsMap.get(`${index}-CP`) || false, stk: checkedItemsMap.get(`${index}-STK`) || false, ta: checkedItemsMap.get(`${index}-TA`) || false, stkVariant: stkVariant, taVariant: taVariant }]
          };
        });
      };
      const quoteData = {
        quote_number: quoteNumber,
        customer: customerName,
        selected_contact: selectedContact || buildPrimaryContact(selectedCustomer),
        date: new Date(getTodayDate()),
        orientation: orientation,
        saw_id_1039: selectedSaw?._id || '',
        brochure: quoteBrochure || '',
        quote_price: formFields.quotePrice || 0,
        standard_discount: parseFloat(formFields.standard_discount) || 0,
        grand_total: computeGrandTotal({
          parts: sampleData,
          installationTotal: quoteDetails.installation?.installationTotal,
          taxStr: quoteDetails.tax,
          defaultDiscount: formFields.standard_discount,
        }),
        customer_rfq_number: formFields.customerRFQ || '',
        infeed_conveyor_style: leftBoxConveyorStyle,
        outfeed_conveyor_style: rightBoxConveyorStyle,
        infeed_modules: generateModuleData(leftBoxItems, leftCheckedItems, leftBoxConveyorStyle, leftBoxRollerLength, 'left'),
        outfeed_modules: generateModuleData(rightBoxItems, rightCheckedItems, rightBoxConveyorStyle, rightBoxRollerLength, 'right'),
        installation: [{ 
          Delivery_Method: quoteDetails.installation?.deliveryMethod, 
          Installation_Option: quoteDetails.installation?.installationOption, 
          Number_of_Service_Technicians: quoteDetails.installation?.techs || '1', 
          Number_of_Days: quoteDetails.installation?.days || '1', 
          Hourly_Rate: quoteDetails.installation?.dayRate || '',
          Lodging_Meals: quoteDetails.installation?.lodgingMeals || '', 
          Car_Rental: quoteDetails.installation?.carRental || '', 
          Tool_Shipments: quoteDetails.installation?.toolShipments || '', 
          Airfare: quoteDetails.installation?.airfare || '', 
          Mileage: quoteDetails.installation?.mileage || '',
          Travel_Time_Cost: quoteDetails.installation?.travelTimeCost || '',
          Conveyor_Shipping_Price: quoteDetails.installation?.conveyorShippingPrice || '',
          Total_Price: quoteDetails.installation?.installationTotal || '', 
          Warranty: quoteDetails.warranty || '', 
          Sales_Tax: quoteDetails.tax || '', 
          Payment_Terms: quoteDetails.payment || '', 
          Saw_Delivery_Schedule: quoteDetails.sawDelivery || '', 
          Conveyor_Delivery_Schedule: quoteDetails.conveyorDelivery || '', 
          Conveyor_Color: quoteDetails.color || '', 
          Inspection_and_Acceptance: quoteDetails.inspection || '', 
          INSTALL_QUOTED: quoteDetails.installation?.installQuoted || '',
          ADDRESS: quoteDetails.installation?.address || '',
          CITY: quoteDetails.installation?.city || '',
          STATE: quoteDetails.installation?.state || '',
          ZIPCODE: quoteDetails.installation?.zipcode || ''
        }],
        colors: quoteDetails.color ? [quoteDetails.color] : [],
        payment_terms: quoteDetails.payment ? [quoteDetails.payment] : [],
        babs_incoterms: quoteDetails.deliveryTerms ? [quoteDetails.deliveryTerms] : [],
        saw_delivery: quoteDetails.sawDelivery ? [quoteDetails.sawDelivery] : [],
        conveyor_delivery: quoteDetails.conveyorDelivery ? [quoteDetails.conveyorDelivery] : [],
        // Persist user-added rows only ("+ New Item" entries). Conveyor-
        // derived rows rebuild on load from infeed_modules/outfeed_modules,
        // so we never persist them in `parts` — keeps mongo doc minimal and
        // avoids double-source-of-truth drift.
        parts: serializeUserPartsForSave(sampleData),
        createdBy: user?.name || 'Unknown',
        createdAt: new Date().toISOString()
      };
      const result = await createQuote(quoteData);
      if (result.success) {
        toast.success('Quote created successfully!');
        const newQuoteNumber = result.data?.quote_number || quoteNumber;
        // Mirror user-added rows to localStorage keyed by the canonical
        // quote_number the server returned. Survives navigation to
        // update-quote and a hard reload.
        saveUserPartsToLocal(newQuoteNumber, sampleData);
        if (typeof onSuccess === 'function') {
          // Caller orchestrates the follow-up (e.g., PDF generation, then form reset).
          await onSuccess(newQuoteNumber, result);
        } else {
          resetCreateForm();
        }
      } else {
        toast.error(result.error || 'Failed to create quote');
      }
      return result;
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('An unexpected error occurred. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const calculateModuleLength = (alt) => {
    if (alt.startsWith('SP-')) return parseInt(alt.split('-')[1]) || 0;
    if (alt === '10FT' || alt === '10FTFORK') return 10;
    if (alt === '5FT' || alt === 'LFT') return 5;
    return 0;
  };

  const handleCreateQuote = async (qd) => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return { success: false, error: 'Customer not selected' }; }
    if (!quoteNumber) { toast.error('Quote number is missing'); return { success: false, error: 'Missing quote number' }; }
    try {
      const customerData = { _id: selectedCustomer._id, customer: selectedCustomer.customer || selectedCustomer.customerData?.customer, title: selectedCustomer.title || selectedCustomer.customerData?.title, phone: selectedCustomer.phone || selectedCustomer.customerData?.phone, email: selectedCustomer.email || selectedCustomer.customerData?.email };
      const quoteData = { quote_number: quoteNumber, customer: customerData, date: new Date(formFields.date || getTodayDate()), orientation, customerRFQ: formFields.customerRFQ, saw_id_1039: selectedSaw?._id || '', sawDetails: { type: formFields.sawType, workBedHeight: formFields.workBedHeight, datumSide: formFields.datumSide, model: selectedSaw ? `${selectedSaw.brand} - ${selectedSaw.model}` : '' } };
      const result = await createQuote(quoteData);
      if (result.success) { toast.success('Quote created successfully!'); return result; } else { toast.error(result.error || 'Failed to create quote'); return { success: false, error: result.error }; }
    } catch (error) { console.error('Error creating quote:', error); toast.error('An unexpected error occurred. Please try again.'); return { success: false, error: error.message }; }
  };

  const handleSubmitData = async (tableData) => {
    const qd = { quoteNumber, customer: selectedCustomer, customerRFQ: formFields.customerRFQ, quotePrice: formFields.quotePrice, date: getTodayDate(), selectedSaw, sawType: formFields.sawType, workBedHeight: formFields.workBedHeight, datumSide: formFields.datumSide, rollerLength: selectedRollerLength, conveyorStyle: selectedConveyorStyle, orientation, infeedLength, outfeedLength, leftBoxItems, rightBoxItems, checkedItems, isReversed, parts: tableData, createdBy: user?.name || 'Unknown', createdAt: new Date().toISOString() };
    handleCreateQuote(qd);
  };

  const handleInputChange = (field, value) => {
    setFormFields(prev => ({ ...prev, [field]: value }));
  };

  // const handleRollerLengthSelect = (value) => {
  //   setSelectedRollerLength(value);
  //   if (activeBox === 'left') { setLeftBoxRollerLength(value); } else { setRightBoxRollerLength(value); }
  // };
  const handleRollerLengthSelect = (value) => {
    setSelectedRollerLength(value);
    // Apply roller length to both boxes
    setLeftBoxRollerLength(value);
    setRightBoxRollerLength(value);
  };

  const handleAddNew = () => {
    const newItem = { id: Date.now(), partName: '', description: '', qty: '', listPrice: '', discount: '', actualPrice: '', isNew: true, isEditing: true, isUserAdded: true, userSection: 'new' };
    setSampleData(prev => [...prev, newItem]);
    setIsAddingNew(true);
  };

  const getCurrentRollerLengths = useCallback(() => {
    if (!selectedConveyorStyle) return [];
    const styleValue = typeof selectedConveyorStyle === 'string' ? selectedConveyorStyle : selectedConveyorStyle.value;
    const styleMap = { 'HEAVY DUTY': 'HD', 'EXTREME DUTY': 'EX', 'SIGMA STYLE': 'SG', 'HD': 'HD', 'EX': 'EX', 'SG': 'SG' };
    const styleKey = styleMap[styleValue.toUpperCase()] || 'DEFAULT';
    const lengths = dropdownOptions.rollerLengths[styleKey] || dropdownOptions.rollerLengths.DEFAULT;
    return lengths;
  }, [selectedConveyorStyle, dropdownOptions.rollerLengths]);

  // ==============================================
  // UPDATE QUOTE: Fetch existing quote data when quoteId present
  // ==============================================
  useEffect(() => {
    const fetchQuoteData = async () => {
      if (!quoteId) return;
      try {
        const quoteRes = await getQuoteById(quoteId);
        if (quoteRes.success) {
          const quote = quoteRes.data;
          setQuoteNumber(quote.quote_number || '');
          setFormFields({
            datumSide: quote.sawDetails?.datumSide || '',
            workBedHeight: quote.sawDetails?.workBedHeight?.toString() || '',
            price: quote.sawDetails?.price?.toString() || '',
            sawType: quote.sawDetails?.sawType || '',
            quotePrice: quote.quote_price?.toString() || '',
            customerRFQ: quote.customer_rfq_number || '',
            standard_discount: quote.customer?.standard_discount || '',
            date: quote.date ? new Date(quote.date).toISOString().split('T')[0] : getTodayDate()
          });

          // Per-quote brochure (independent of the saw's own brochure).
          setQuoteBrochure(quote.brochure || '');

          // quote.customer can be a legacy string ("ACME Corp") or the newer
          // object shape ({ _id, customer, title, phone, email }) saved by
          // handleCreateQuote. Normalize to a name before looking up the full
          // customer record so we can hydrate the standard_discount field.
          const quoteCustomerName = typeof quote.customer === 'string'
            ? quote.customer
            : (quote.customer?.customer || quote.customer?.name || '');
          if (quoteCustomerName) {
            const customer = customers.find(c => c.customer === quoteCustomerName || (c.customerData && c.customerData.customer === quoteCustomerName));
            if (customer) {
              setSelectedCustomer(customer);
              setSelectedCustomerName(customer.customer || customer.customerData?.customer);
              setFormFields(prev => ({ ...prev, standard_discount: customer.standard_discount || '' }));

              // Restore saved selected_contact if present, else default to the primary contact
              if (quote.selected_contact && quote.selected_contact.name) {
                setSelectedContact(quote.selected_contact);
              } else {
                setSelectedContact(buildPrimaryContact(customer));
              }

              // Seed additional-emails list from the customer's locations so the
              // emails dropdown works on the update flow.
              const emails = (customer.additional_locations || [])
                .map(l => (l.email || '').trim())
                .filter(Boolean);
              setAdditionalEmails(emails);
            }
          }

          if (quote.saw_id_1039) {
            const saw = saws.find(s => s._id === quote.saw_id_1039);
            if (saw) {
              setSelectedSaw(saw);
              setFormFields(prev => ({ ...prev, datumSide: saw.datum_side || '', workBedHeight: saw.work_bed_height?.toString() || '', sawType: saw.saw_type || '', price: saw.price?.toString() || prev.price }));
              if (saw.image_name) {
                const sawImageRes = await getSawImageData(saw.image_name);
                if (sawImageRes.success) { setSawImage(sawImageRes.url); }
              }
            }
          }

          const infeedStyle = quote.infeed_modules?.[0]?.style || '';
          const outfeedStyle = quote.outfeed_modules?.[0]?.style || '';
          setLeftBoxConveyorStyle(infeedStyle);
          setRightBoxConveyorStyle(outfeedStyle);

          setOrientation(quote.orientation || 'left-to-right');
          setIsRotatedArrow(quote.orientation === 'right-to-left');
          
          if (quote.orientation === 'right-to-left') { setItems(['Outfeed', 'Material Flow', 'Infeed']); } 
          else { setItems(['Infeed', 'Material Flow', 'Outfeed']); }

          const leftItems = [];
          const leftChecked = new Map();
          quote.infeed_modules?.forEach((module, index) => {
            // Restore spacer width if this is a spacer
            const isSpacer = module.module_type?.startsWith('SP-');
            const isAngled = module.module_type === ATBL_ALT;
            const spacerItem = isSpacer
              ? { type: 'option', alt: module.module_type, labels: [], width: module.module_width || module.module_length || 0, height: '100%', fixedWidth: true }
              : isAngled
                ? { type: 'option', alt: module.module_type, labels: [] }
                : { type: 'option', alt: module.module_type, labels: ALL_ITEM_LABELS };
            leftItems.push(spacerItem);
            // Derive option toggle state from filename + *_yn first, then
            // module_options[0] (the latter was vestigial for VR/CP/MT/PW
            // prior to the 2026-05-13 backend fix).
            const derived = deriveModuleOptionStates(module);
            if (derived.pw) leftChecked.set(`${index}-PW`, true);
            if (derived.vr) leftChecked.set(`${index}-VR`, true);
            if (derived.cp) leftChecked.set(`${index}-CP`, true);
            if (derived.mt) leftChecked.set(`${index}-MT`, true);
            if (derived.stk) leftChecked.set(`${index}-STK`, true);
            if (derived.ta) leftChecked.set(`${index}-TA`, true);
            if (derived.ta) { setTASelections(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: true } })); }
            if (derived.stk) { setSTKSelections(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: true } })); }
            if (derived.stkVariant) { setSTKVariantByPortion(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: derived.stkVariant } })); }
            if (derived.taVariant) { setTAVariantByPortion(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: derived.taVariant } })); }
          });
          if (quote.infeed_modules && quote.infeed_modules.length > 0 && typeof quote.infeed_modules[0].flips === 'boolean') {
            setFlippedBoxes(prev => ({ ...prev, left: !!quote.infeed_modules[0].flips }));
            setFilenameFlipStates(prev => ({ ...prev, left: !!quote.infeed_modules[0].flips }));
          }
          setLeftBoxItems(leftItems);
          setCheckedItems(prev => ({ ...prev, left: leftChecked }));

          const rightItems = [];
          const rightChecked = new Map();
          quote.outfeed_modules?.forEach((module, index) => {
            // Restore spacer width if this is a spacer
            const isSpacer = module.module_type?.startsWith('SP-');
            const isAngled = module.module_type === ATBL_ALT;
            const spacerItem = isSpacer
              ? { type: 'option', alt: module.module_type, labels: [], width: module.module_width || module.module_length || 0, height: '100%', fixedWidth: true }
              : isAngled
                ? { type: 'option', alt: module.module_type, labels: [] }
                : { type: 'option', alt: module.module_type, labels: ALL_ITEM_LABELS };
            rightItems.push(spacerItem);
            const derived = deriveModuleOptionStates(module);
            if (derived.pw) rightChecked.set(`${index}-PW`, true);
            if (derived.vr) rightChecked.set(`${index}-VR`, true);
            if (derived.cp) rightChecked.set(`${index}-CP`, true);
            if (derived.mt) rightChecked.set(`${index}-MT`, true);
            if (derived.stk) rightChecked.set(`${index}-STK`, true);
            if (derived.ta) rightChecked.set(`${index}-TA`, true);
            if (derived.ta) { setTASelections(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: true } })); }
            if (derived.stk) { setSTKSelections(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: true } })); }
            if (derived.stkVariant) { setSTKVariantByPortion(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: derived.stkVariant } })); }
            if (derived.taVariant) { setTAVariantByPortion(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: derived.taVariant } })); }
          });
          if (quote.outfeed_modules && quote.outfeed_modules.length > 0 && typeof quote.outfeed_modules[0].flips === 'boolean') {
            setFlippedBoxes(prev => ({ ...prev, right: !!quote.outfeed_modules[0].flips }));
            setFilenameFlipStates(prev => ({ ...prev, right: !!quote.outfeed_modules[0].flips }));
          }
          setRightBoxItems(rightItems);
          setCheckedItems(prev => ({ ...prev, right: rightChecked }));

          const infeedLen = quote.infeed_modules?.reduce((sum, module) => sum + module.module_length, 0) || 0;
          const outfeedLen = quote.outfeed_modules?.reduce((sum, module) => sum + module.module_length, 0) || 0;
          setInfeedLength(infeedLen);
          setOutfeedLength(outfeedLen);
          if (quote.infeed_modules && quote.infeed_modules.length > 0) { setLeftBoxRollerLength(quote.infeed_modules[0].module_roller_length || ''); }
          if (quote.outfeed_modules && quote.outfeed_modules.length > 0) { setRightBoxRollerLength(quote.outfeed_modules[0].module_roller_length || ''); }

          quote.infeed_modules?.forEach((module, index) => {
            const derived = deriveModuleOptionStates(module);
            if (derived.mt && derived.mtVariant) { setMotorVariantByBox(prev => ({ ...prev, left: derived.mtVariant })); setMotorSelections(prev => ({ ...prev, left: index.toString() })); }
            if (derived.stkVariant) { setSTKVariantByPortion(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: derived.stkVariant } })); }
            if (derived.taVariant) { setTAVariantByPortion(prev => ({ ...prev, left: { ...(prev.left || {}), [index]: derived.taVariant } })); }
          });
          quote.outfeed_modules?.forEach((module, index) => {
            const derived = deriveModuleOptionStates(module);
            if (derived.mt && derived.mtVariant) { setMotorVariantByBox(prev => ({ ...prev, right: derived.mtVariant })); setMotorSelections(prev => ({ ...prev, right: index.toString() })); }
            if (derived.stkVariant) { setSTKVariantByPortion(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: derived.stkVariant } })); }
            if (derived.taVariant) { setTAVariantByPortion(prev => ({ ...prev, right: { ...(prev.right || {}), [index]: derived.taVariant } })); }
          });

          // Defer the label-replay (PW/VR/CP/MT/TA/STK → table rows) until
          // after leftBoxItems/rightBoxItems are committed; see the
          // pendingQuoteReplayRef useEffect below for the actual replay.
          pendingQuoteReplayRef.current = quote;

          // Restore user-added line items (rows the user added via the "+ New
          // Item" button) including their drag-and-drop placement. Conveyor-
          // derived rows are rebuilt by the replay above from infeed_modules/
          // outfeed_modules, so we only seed sampleData with `isUserAdded`
          // entries here.
          //
          // Source priority:
          //   1. `quote.parts` from the server (preferred — single source of
          //      truth once the backend persists it).
          //   2. `localStorage` keyed by quote_number (fallback while the
          //      backend catches up, so save→reload still works on the same
          //      browser).
          // Every entry in `quote.parts` is — by save-side contract — a
          // user-added row (serializeUserPartsForSave filters to isUserAdded).
          // Re-stamp `isUserAdded:true` on load so a strict-mode mongoose
          // schema that strips the flag still produces usable rows.
          const serverUserParts = Array.isArray(quote.parts)
            ? quote.parts
                .filter(p => p && p.id != null)
                .map(p => ({ ...p, isUserAdded: true }))
            : [];
          const localUserParts = loadUserPartsFromLocal(quote.quote_number);
          const mergedById = new Map();
          // localStorage entries come first; server entries override them on
          // id collision so the persisted record always wins.
          localUserParts.forEach(p => mergedById.set(p.id, p));
          serverUserParts.forEach(p => mergedById.set(p.id, p));
          const userPartsToRestore = Array.from(mergedById.values());
          if (userPartsToRestore.length > 0) {
            setSampleData(prev => {
              const existingIds = new Set(prev.map(p => p.id));
              const merged = [...prev];
              userPartsToRestore.forEach(p => {
                if (!existingIds.has(p.id)) merged.push(p);
              });
              return merged;
            });
          }

          if (quote.installation && quote.installation.length > 0) {
            const install = quote.installation[0];
            setQuoteDetails({
              color: quote.colors?.[0] || '',
              sawDelivery: quote.saw_delivery?.[0] || '',
              conveyorDelivery: quote.conveyor_delivery?.[0] || '',
              deliveryTerms: quote.babs_incoterms?.[0] || '',
              inspection: install.Inspection_and_Acceptance || '',
              warranty: install.Warranty || '',
              tax: install.Sales_Tax || '',
              payment: quote.payment_terms?.[0] || '',
              location: '',
              installation: {
                techs: install.Number_of_Service_Technicians || '1',
                days: install.Number_of_Days || '1',
                laborTotal: install.Hourly_Rate || '',
                installationTotal: install.Total_Price || '',
                showDetails: false,
                deliveryMethod: install.Delivery_Method || '',
                installationOption: install.Installation_Option || '',
                installQuoted: install.INSTALL_QUOTED || '',
                dayRate: install.Hourly_Rate || '',
                lodgingMeals: install.Lodging_Meals || '',
                carRental: install.Car_Rental || '',
                toolShipments: install.Tool_Shipments || '',
                airfare: install.Airfare || '',
                mileage: install.Mileage || '',
                travelTimeCost: install.Travel_Time_Cost || '',
                conveyorShippingPrice: install.Conveyor_Shipping_Price || '',
                address: install.ADDRESS || '',
                city: install.CITY || '',
                state: install.STATE || '',
                zipcode: install.ZIPCODE || ''
              }
            });
          }
        } else {
          toast.error('Failed to fetch quote data');
          navigate('/quotes');
        }
      } catch (error) {
        console.error('Error fetching quote:', error);
        toast.error('An error occurred while fetching quote data');
      }
    };
    if (customers.length > 0 && saws.length > 0) { fetchQuoteData(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, customers, saws]);

  // Replay label-option / special-label handlers AFTER leftBoxItems and
  // rightBoxItems have been committed, so handleLabelChange and
  // upsertLabelOptionRow read the populated box arrays from their closures
  // instead of bailing at the empty-items guard.
  useEffect(() => {
    const quote = pendingQuoteReplayRef.current;
    if (!quote) return;
    const expectedLeft = quote.infeed_modules?.length || 0;
    const expectedRight = quote.outfeed_modules?.length || 0;
    if ((leftBoxItems?.length || 0) < expectedLeft) return;
    if ((rightBoxItems?.length || 0) < expectedRight) return;
    pendingQuoteReplayRef.current = null;

    quote.infeed_modules?.forEach((module, index) => {
      const derived = deriveModuleOptionStates(module);
      if (derived.mt && derived.mtVariant) { try { handleMotorSelect('left', index.toString(), derived.mtVariant); } catch (e) { /* noop */ } }
      if (derived.ta) { try { handleLabelChange('left', index, 'TA', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.stk) { try { handleLabelChange('left', index, 'STK', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.pw) { try { handleLabelChange('left', index, 'PW', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.vr) { try { handleLabelChange('left', index, 'VR', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.cp) { try { handleLabelChange('left', index, 'CP', true, 'static'); } catch (e) { /* noop */ } }
    });
    quote.outfeed_modules?.forEach((module, index) => {
      const derived = deriveModuleOptionStates(module);
      if (derived.mt && derived.mtVariant) { try { handleMotorSelect('right', index.toString(), derived.mtVariant); } catch (e) { /* noop */ } }
      if (derived.ta) { try { handleLabelChange('right', index, 'TA', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.stk) { try { handleLabelChange('right', index, 'STK', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.pw) { try { handleLabelChange('right', index, 'PW', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.vr) { try { handleLabelChange('right', index, 'VR', true, 'static'); } catch (e) { /* noop */ } }
      if (derived.cp) { try { handleLabelChange('right', index, 'CP', true, 'static'); } catch (e) { /* noop */ } }
    });
  }, [leftBoxItems, rightBoxItems, handleLabelChange, handleMotorSelect]);

  // ==============================================
  // UPDATE QUOTE: Submit update
  // ==============================================
  const handleUpdateQuoteButton = async (onSuccess) => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return { success: false }; }
    if (!quoteNumber) { toast.error('Quote number is missing'); return { success: false }; }
    try {
      const customerName = selectedCustomer.customer || selectedCustomer.customerData?.customer || 'Unknown Customer';
      const generateModuleData = (items, checkedItemsMap, conveyorStyle, rollerLength, boxName) => {
        return items.map((item, index) => {
          const selectedLabels = item.labels.filter(label => { const key = `${index}-${label}`; return checkedItemsMap.get(key) === true; });
          const filename = generateConveyorFilename(selectedLabels, item.alt, conveyorStyle, boxName);
          let motorVariant = '';
          if (motorSelections[boxName] === index.toString()) { motorVariant = motorVariantByBox[boxName] || ''; }
          const hasMotor = checkedItemsMap.get(`${index}-MT`) || false;
          const hasVerticalRoller = checkedItemsMap.get(`${index}-VR`) || false;
          const hasCoolantPan = checkedItemsMap.get(`${index}-CP`) || false;
          let motorHpValue = '';
          if (hasMotor && motorSelections[boxName] === index.toString()) {
            const motorVariantType = motorVariantByBox[boxName];
            if (motorVariantType === 'motor') motorHpValue = '5HP'; else if (motorVariantType === 'motor1') motorHpValue = '3HP'; else if (motorVariantType === 'motor2') motorHpValue = '7.5HP';
          }
          // Get STK variant if STK is selected
          const hasSTK = checkedItemsMap.get(`${index}-STK`) || false;
          const stkVariant = hasSTK ? (stkVariantByPortion?.[boxName]?.[index] || '') : '';
          // Get TA variant if TA is selected
          const hasTAOpt = checkedItemsMap.get(`${index}-TA`) || false;
          const taVariantOpt = hasTAOpt ? (taVariantByPortion?.[boxName]?.[index] || '') : '';
          // Get spacer width if this is a spacer
          const isSpacer = item.alt.startsWith('SP-');
          const spacerWidth = isSpacer ? (item.width || calculateModuleLength(item.alt) || 0) : undefined;
          return { module_type: item.alt, module_length: calculateModuleLength(item.alt), module_width: spacerWidth, style: conveyorStyle, module_roller_length: rollerLength, filename, flips: !!flippedBoxes[boxName], motor_yn: hasMotor ? 'YES' : 'NO', motor_hp_value: motorHpValue, vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO', coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO', module_options: [{ pw: checkedItemsMap.get(`${index}-PW`) || false, vr: checkedItemsMap.get(`${index}-VR`) || false, mt: motorVariant, cp: checkedItemsMap.get(`${index}-CP`) || false, stk: checkedItemsMap.get(`${index}-STK`) || false, ta: checkedItemsMap.get(`${index}-TA`) || false, stkVariant: stkVariant, taVariant: taVariantOpt }] };
        });
      };
      const quoteData = {
        _id: quoteId,
        quote_number: quoteNumber,
        customer: customerName,
        selected_contact: selectedContact || buildPrimaryContact(selectedCustomer),
        date: new Date(getTodayDate()),
        orientation: orientation,
        quote_price: formFields.quotePrice || 0,
        standard_discount: parseFloat(formFields.standard_discount) || 0,
        grand_total: computeGrandTotal({
          parts: sampleData,
          installationTotal: quoteDetails.installation?.installationTotal,
          taxStr: quoteDetails.tax,
          defaultDiscount: formFields.standard_discount,
        }),
        customerRFQ: formFields.customerRFQ,
        customer_rfq_number: formFields.customerRFQ || '',
        infeed_conveyor_style: leftBoxConveyorStyle,
        outfeed_conveyor_style: rightBoxConveyorStyle,
        saw_id_1039: selectedSaw?._id || '',
        brochure: quoteBrochure || '',
        infeed_modules: generateModuleData(leftBoxItems, checkedItems.left, leftBoxConveyorStyle, leftBoxRollerLength, 'left'),
        outfeed_modules: generateModuleData(rightBoxItems, checkedItems.right, rightBoxConveyorStyle, rightBoxRollerLength, 'right'),
        installation: [{ 
          Delivery_Method: quoteDetails.installation?.deliveryMethod, 
          Installation_Option: quoteDetails.installation?.installationOption, 
          Number_of_Service_Technicians: quoteDetails.installation?.techs || '1', 
          Number_of_Days: quoteDetails.installation?.days || '1', 
          Hourly_Rate: quoteDetails.installation?.dayRate || '',
          Lodging_Meals: quoteDetails.installation?.lodgingMeals || '', 
          Car_Rental: quoteDetails.installation?.carRental || '', 
          Tool_Shipments: quoteDetails.installation?.toolShipments || '', 
          Airfare: quoteDetails.installation?.airfare || '', 
          Mileage: quoteDetails.installation?.mileage || '',
          Travel_Time_Cost: quoteDetails.installation?.travelTimeCost || '',
          Conveyor_Shipping_Price: quoteDetails.installation?.conveyorShippingPrice || '',
          Total_Price: quoteDetails.installation?.installationTotal || '', 
          Warranty: quoteDetails.warranty || '', 
          Sales_Tax: quoteDetails.tax || '', 
          Payment_Terms: quoteDetails.payment || '', 
          Saw_Delivery_Schedule: quoteDetails.sawDelivery || '', 
          Conveyor_Delivery_Schedule: quoteDetails.conveyorDelivery || '', 
          Conveyor_Color: quoteDetails.color || '', 
          Inspection_and_Acceptance: quoteDetails.inspection || '', 
          INSTALL_QUOTED: quoteDetails.installation?.installQuoted || '',
          ADDRESS: quoteDetails.installation?.address || '',
          CITY: quoteDetails.installation?.city || '',
          STATE: quoteDetails.installation?.state || '',
          ZIPCODE: quoteDetails.installation?.zipcode || ''
        }],
        colors: quoteDetails.color ? [quoteDetails.color] : [],
        payment_terms: quoteDetails.payment ? [quoteDetails.payment] : [],
        babs_incoterms: quoteDetails.deliveryTerms ? [quoteDetails.deliveryTerms] : [],
        saw_delivery: quoteDetails.sawDelivery ? [quoteDetails.sawDelivery] : [],
        conveyor_delivery: quoteDetails.conveyorDelivery ? [quoteDetails.conveyorDelivery] : [],
        // Persist user-added rows only — see serializeUserPartsForSave above.
        parts: serializeUserPartsForSave(sampleData),
        updatedBy: user?.name || 'Unknown',
        updatedAt: new Date().toISOString()
      };
      const result = await updateQuoteVersion(quoteNumber, quoteData);
      if (result.success) {
        toast.success('Quote updated successfully!');
        // Server bumps the version (e.g. RR05112026.1.1 -> .1.2). Sync the local
        // quote number so any subsequent action (PDF generation, etc.) uses the
        // new version key rather than the now-stale one.
        const newQuoteNumber = result.data?.quote_number || quoteNumber;
        if (newQuoteNumber && newQuoteNumber !== quoteNumber) setQuoteNumber(newQuoteNumber);
        // Mirror user-added rows to localStorage under the new version key so
        // the next load (whether through update-quote or a refresh) restores
        // them — and clear the old version's key so it doesn't drift.
        carryUserPartsToNewVersion(quoteNumber, newQuoteNumber, sampleData);
        if (typeof onSuccess === 'function') {
          await onSuccess(newQuoteNumber, result);
        } else {
          navigate('/quotes');
        }
      } else {
        const errorMessage = result.error?.message || result.message || 'Failed to update quote';
        toast.error(errorMessage);
      }
      return result;
    } catch (error) {
      console.error('Error updating quote:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Exit handler for the quote builder's "Exit" button (wired as onExit on
  // TableComponent). It previously only closed the unrelated custom-value modal
  // (setCustomModalOpen), so confirming Exit dismissed the dialog but never left the
  // page. Navigate to /quotes — the same destination every other "leave the quote"
  // path uses (see navigate('/quotes') at the create/update success handlers).
  const handleCloseModal = () => { setCustomModalOpen(false); navigate('/quotes'); };

  const handleSpacerConfirm = (value) => {
    const currentItems = isRotatedBox ? rightBoxItems : leftBoxItems;
    const currentLength = calculateTotalLength(currentItems);
    const spacerValue = parseInt(value) || 0;
    if (currentLength + spacerValue > LENGTH_LIMIT_MAX_FT) {
      const remaining = LENGTH_LIMIT_MAX_FT - currentLength;
      setLengthLimitMessage(<>Cannot add {spacerValue}ft spacer. Maximum length is {LENGTH_LIMIT_MAX_FT}ft.<br />Only <span className="text-red-600 font-bold">{remaining}</span> ft remaining!</>);
      setShowLengthLimitModal(true);
      return;
    }
    const validatedValue = validateLength(spacerValue);
    const spacerItem = { type: 'option', alt: `SP-${validatedValue}`, labels: [], width: validatedValue, height: '100%', fixedWidth: true };
    const boxSetter = isRotatedBox ? setRightBoxItems : setLeftBoxItems;
    const lengthSetter = isRotatedBox ? setOutfeedLength : setInfeedLength;
    boxSetter((prev) => { const newItems = [...prev, spacerItem]; const totalLength = calculateTotalLength(newItems); lengthSetter(validateLength(totalLength)); return newItems; });
  };

  const handleTableSave = (updatedItem) => {
    if (updatedItem._delete) { setSampleData((prev) => prev.filter((item) => item.id !== updatedItem.id)); }
    else { setSampleData((prev) => prev.map((item) => (item.id === updatedItem.id ? updatedItem : item))); }
    setIsAddingNew(false);
  };

  const handleCancelAddNew = (id) => {
    setSampleData((prev) => prev.filter((item) => item.id !== id));
    setIsAddingNew(false);
  };
  const handleClearTable = () => { setSampleData([]); };
  const handleSTKChange = async (boxName, portionIndex, value) => {
    const variant = value?.target?.value || value;

    // Update the variant selection
    setSTKVariantByPortion(prev => ({
      ...prev,
      [boxName]: {
        ...(prev[boxName] || {}),
        [portionIndex]: variant
      }
    }));

    // Check if STK is currently selected for this specific portion
    const key = `${portionIndex}-STK`;
    const isStkSelected = checkedItems[boxName]?.get(key) === true;

    if (isStkSelected) {
      // Remove the old STK entry for this specific portion and add a new one with the updated variant
      removeSpecialLabelFromQuote(boxName, 'STK', portionIndex);

      const isFlipped = filenameFlipStates[boxName];

      // Try preloaded stkDetails first
      let detailsKey;
      if (variant === '5FT') detailsKey = isFlipped ? 'low' : 'high';
      else if (variant === '10FT') detailsKey = isFlipped ? 'low10FT' : 'high10FT';
      else if (variant === '15FT') detailsKey = isFlipped ? 'low15FT' : 'high15FT';
      else if (variant === '20FT') detailsKey = isFlipped ? 'low20FT' : 'high20FT';

      let conveyorData = stkDetails[boxName]?.[detailsKey] || null;

      if (!conveyorData) {
        // Fallback to API fetch — STK filenames are universal
        const stkSize = variant.replace('FT', '');
        const stkHeight = isFlipped ? 'LOW' : 'HIGH';
        const filename = `BABS-${stkSize}-STKA-BO-${stkHeight}.jpg`;

        try {
          const response = await getImageWithDetails(filename);
          conveyorData = response?.conveyorData || null;
        } catch (error) {
          console.error('Error updating STK details:', error);
        }
      }

      if (conveyorData) {
        addSpecialLabelToQuote(boxName, 'STK', conveyorData, portionIndex);
      }
    }
  };

  const handleTAChange = async (boxName, portionIndex, value) => {
    const variant = value?.target?.value || value;

    setTAVariantByPortion(prev => ({
      ...prev,
      [boxName]: {
        ...(prev[boxName] || {}),
        [portionIndex]: variant
      }
    }));

    const key = `${portionIndex}-TA`;
    const isTASelected = checkedItems[boxName]?.get(key) === true;

    if (isTASelected) {
      removeSpecialLabelFromQuote(boxName, 'TA', portionIndex);

      const prefix = getRollerPrefixForBox(boxName);
      const filename = prefix + getTAFilenamesForVariant(variant, filenameFlipStates[boxName]);

      try {
        const response = await getImageWithDetails(filename);
        if (response.conveyorData) {
          addSpecialLabelToQuote(boxName, 'TA', response.conveyorData, portionIndex);
        }
      } catch (error) {
        console.error('Error updating TA details:', error);
      }
    }
  };

  return {
    // refs
    topRef,
    // state/values
    quoteNumber, formFields, customers, selectedCustomerName, dropdownOptions, selectedConveyorStyle, selectedRollerLength, sawOptions, infeedLength, outfeedLength, items, isRotatedArrow, orientation, isFlipped, leftBoxItems, rightBoxItems, isRotatedBox, checkedItems, leftBoxConveyorStyle, rightBoxConveyorStyle, isReversed, flippedBoxes, motorSelections, taSelections, filenameFlipStates, stkSelections, globalImageCache, leftBoxCacheClear, rightBoxCacheClear, motorVariantImageUrls, taImageUrls, stkImageUrls, motorVariantByBox, sawImage, selectedSaw,setSelectedSaw, selectedCustomer, isAddingNew, customModalOpen, isQuoteCreated, showLengthLimitModal, lengthLimitMessage, showQuoteDetailsModal, showSpacerModal, sampleData, buttonsEnabled, quoteDetails,motorDetailsByBox, taDetails, stkVariantByPortion, taVariantByPortion, additionalEmails, selectedAdditionalEmails, selectedContact, quoteBrochure,
    // setters
    setInfeedLength, setLengthLimitMessage, setShowLengthLimitModal, setOutfeedLength, setIsFlipped, setIsRotatedBox, setGlobalImageCache, setActiveBox, setShowQuoteDetailsModal, setCustomModalOpen, setQuoteDetails, setShowSpacerModal,setSampleData,motorVariantDetails,stkDetails, setSelectedAdditionalEmails, setSelectedContact, setQuoteBrochure,
    // helpers/handlers
    getTodayDate, handleInputChange, handleCustomerSelection, handleConveyorStyleSelection, getCurrentRollerLengths, handleRollerLengthSelect, handleSawSelection, handleRemoveSaw, handleAddNewSaw, handleArrowClick, handleVerticalFlip, handleCheckboxChange, handleBoxItemsChange, handleCheckedItemsChange, generateConveyorFilename, getImageForSelection, getImageKey, handleMotorSelect, handleAddNew, handleTableSave, handleClearTable, handleCloseModal, handleSubmitData, handleCreateQuoteButton, handleSpacerConfirm, calculateTotalLength, handleCancelAddNew, handle10FtModel, handle5FtModel, handleLiftTable, handleAngledTable, handle10FtForkModel, handleClearBox, handleDeletePortion, handleUpdateQuoteButton, handleSTKChange, handleTAChange, handleAddContact, resetCreateForm
  };
}
